// CustomVoiceInterviewV2Test.tsx
import React, {
    useState,
    useCallback,
    useEffect,
    useRef,
    useImperativeHandle,
    forwardRef,
    ForwardedRef,
    useMemo,
} from "react";
import axios from "axios";
import {
    DailyAudio,
    useDaily,
    useDailyEvent,
    useParticipantIds,
    useReceiveSettings,
    useSendSettings, // Keep if used within VideoContainer or for debugging
} from "@daily-co/daily-react";
import { DailyEventObjectAppMessage } from "@daily-co/daily-js";
import { VideoContainer } from "@component/video-container"; // Adjust path as needed
import type {
    VideoInterviewStatus,
    VideoInterviewRef,
    TavusInteractionCallbacks,
    DailyAppMessageCallback,
    TavusAppMessageData,
    PipecatAppMessageData,
    CustomInterviewSection,
} from "@type/videoInterview"; // Adjust path as needed
import { Button } from "@component/button";
import { InterviewConfirmationModal } from "@component/interview-confirmation-modal";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
} from "@component/dropdown-menu";
import {
    MicrophoneSelector,
    SpeakerSelector,
    CameraSelector,
} from "@component/device-selectors";
import {
    Mic as MicrophoneIcon,
    Clock as ClockIcon,
    VideoOff as VideoOffIcon,
    MicOff as MicOffIcon,
    Loader,
    VideoIcon,
    StarIcon,
    TrendingUpIcon,
    WifiIcon,
    SettingsIcon,
    CheckIcon,
} from "lucide-react";
import {
    createInterview,
    generateCandidateInsights,
    generatePracticeInsights,
    getInterviewStatus,
    updateInterviewStatusV2,
    isClientError,
    resolveConversationId,
} from "@service/interview";
import { useSpeechDetection } from '@context/speech_detection_context';
import { getBrowserName } from "@util/string";
import { formatDuration } from "@util/date";
import { submitAssessmentV2, recordAIInterviewStarted } from "@service/assessment";
import { trackEvent } from "@util/mixpanel";
import { useParams } from "react-router-dom";
import { useSnackbar } from "@hook/use-snackbar";
import { getCookie, setCookie } from "@util/cookie";
import { catchErrorLog } from "@util/sentry";
import { isVirtualAudioDevice } from "@util/audio-device";
import { InterviewEndConfirmDialogV2 } from "@component/confirm-end-interview-v2";
import RetakeRequestDialog from "@component/retake-request-dialog";
import IssuesDialog from "@component/issues-dialog";
import EnhancedAudioVisualizer from "@component/enhanced-audio-visualizer";
import BrowserCompatibilityWarning from "@component/browser-compatibility-warning";
import MicCameraPermissionGuide from "@component/mic-camera-permission-guide";
import { useCompanySubdomain } from "@context/subdomain_context";
import { Tooltip } from "@component/tooltip";
import { cn } from "@util/tailwind";
import { Dialog } from "@radix-ui/react-dialog";
import { DialogContent, DialogHeader, DialogTitle } from "@component/dialog";
import AudioLevelIndicator, { type AudioLevelStatus } from "@component/audio-level-indicator";
import AudioGateModal from "@component/audio-gate-modal";
import WaitingRoomSetupChecks from "@container/waiting-room-setup-checks";

// v2/update-status with ONE retry that NEVER rejects. The completion chain
// (onInterviewStatusChange → submitAssessmentV2 → onCompletedInterview) is
// chained on this promise's .then(), and an unhandled rejection used to
// silently kill ALL of it — status never recorded, parent never notified,
// candidate stranded. If both attempts fail we log to Sentry and still
// resolve so the local flow proceeds; the BE can reconcile from the
// submitted assessment / recording.
const updateInterviewStatusSafe = async (
    payload: Parameters<typeof updateInterviewStatusV2>[0]
): Promise<void> => {
    try {
        await updateInterviewStatusV2(payload);
    } catch (firstErr) {
        // A 4xx means the submission isn't there — retrying can't create it, it only
        // hides the real error and delays the candidate (#3460). Retry network/5xx only.
        if (isClientError(firstErr)) {
            catchErrorLog(`[interview] v2/update-status rejected with a client error — not retrying: ${firstErr}`);
            return;
        }
        catchErrorLog(`[interview] v2/update-status failed, retrying once: ${firstErr}`);
        try {
            await updateInterviewStatusV2(payload);
        } catch (retryErr) {
            catchErrorLog(`[interview] v2/update-status failed after retry — continuing local completion flow: ${retryErr}`);
        }
    }
};

// --- Component Props ---
export interface CustomVoiceInterviewV2Props {
    // Tavus & Backend Configd Config
    pipecatApiUrl: string; // Base API URL
    agentName: string; // Agent name for Pipecat
    authToken: string; // Bearer token for authentication
    s3BucketName: string;
    s3BucketRegion: string;
    awsAssumeRoleArn: string;
    fastapiURL: string;
    userId: string; // Ensure userId is always passed
    userCV?: string; // CV UUID for the user
    // Interaction Callbacks (passed down)
    tavusCallbacks?: TavusInteractionCallbacks;
    userName?: string;
    isSkipInterview?: boolean
    isLoadingSkipInterview?: boolean;
    onSkipInterview?: () => void
    /**
     * When true, a secondary "I will do this later" button is shown in the
     * waiting room next to the Start / I'm having issues actions. Used by
     * the JobseekerSignup flow so candidates can defer the AI interview.
     */
    showSkipLaterButton?: boolean;
    // Modal Control
    onCloseModal: () => void;
    onCompletedInterview: () => void; // Callback when interview completes
    // Unique for Custom Interview
    sections: CustomInterviewSection[]; // Array of interview sections
    interviewName?: string;
    description: string | React.ReactNode; // Interview description
    maxDuration: number;
    interviewContext: string; // AI personality
    closingStatement?: string;
    interviewerPersona?: string;
    toneAndCommunication?: {
        enabled: boolean;
        selectedTones?: string[];
        additionalPreferences?: string;
    };
    interviewFlowPrinciples?: {
        enabled: boolean;
        selectedPrinciples?: string[];
        additionalGuidelines?: string;
    };
    informationToShare?: {
        enabled: boolean;
        value: string;
    };
    customGreeting?: string;    // for generalization
    aiName?: string; // AI name 
    aiLogoSrc?: string; // AI logo source
    interviewId: string
    // Test mode indicator
    isTestInterview?: boolean; // Indicates if this is a test interview
    requireSignIn?: boolean; // Whether to require user sign-in before starting the interview
    userEmail?: string; // User email for interview
    concludingStatements?: string; // Concluding statements for the interview
    job_id?: string; // Job ID for the interview, if applicable
    company_id?: string; // Company ID for fetching interview context
    language?: "English" | "Vietnamese" | "FirstMove" | "Demo" | "Training"; // Language for interview confirmation modal
    sessionMode?: "interview" | "roleplay"; // Selects interview vs roleplay copy. Defaults to "interview".
    leavePromptText?: string; // Override the beforeunload prompt (default: "Leave interview?").
    expiredBodyText?: string; // Override the expired-session body (default: "This interview session has expired.")
    isPracticeInterview?: boolean; // Whether this is a practice interview
    interviewLanguage?: string;
    voice?: "male" | "female" | string;
    voiceKey?: string; // Opaque voice identifier (e.g. training persona voice_key). Forwarded verbatim to Pipecat; overrides `voice` when set.
    /**
     * When set, used verbatim as the Pipecat conversation_id and the
     * `createInterview` registration step is skipped. Training passes the
     * `TrainingSession.id` here so the BE can match the Pipecat transcript
     * callback to the training session for scoring.
     */
    conversationIdOverride?: string;
    idleTimeoutMessage?: string; // Optional idle timeout message
    isDisabledButtonIssue?: boolean;
    handleEndSession?: () => void;
    onReload?: () => void;
    onInterviewStatusChange?: (status: string) => void;
    hasFormAssessment?: boolean;
    hasEligibilityCheck?: boolean;
    // Optional copy overrides for non-recruiter product variants (e.g. training roleplay).
    // Defaults preserve existing recruiter strings.
    actionLabel?: string;
    loadingActionLabel?: string;
    retakeLabel?: string;
    tipText?: string;
    privacyLine1?: string;
    privacyLine2?: string;
    headlineReady?: string;
    expiredHeadline?: string;
    /**
     * Override the 4 readiness bullets shown next to the device preview.
     * Provide exactly 4 entries to keep the existing icon layout (star,
     * trending-up, settings, wifi). When omitted, the recruiter defaults
     * are used.
     */
    readinessBullets?: [string, string, string, string];
    permissionPrompt?: string;
    waitingForAiText?: string;
}


/** Resolve once the <video> is actually rendering frames again after a camera
 *  switch — or after a fallback timeout, so the UI never stays locked if the
 *  `playing` event never arrives (e.g. an acquisition error). */
function waitForVideoPlaying(video: HTMLVideoElement, timeoutMs = 5000): Promise<void> {
    return new Promise((resolve) => {
        // Already playing with at least the current frame decoded.
        if (!video.paused && video.readyState >= 2) {
            resolve();
            return;
        }
        let settled = false;
        const finish = () => {
            if (settled) return;
            settled = true;
            video.removeEventListener("playing", finish);
            video.removeEventListener("canplay", finish);
            clearTimeout(timer);
            resolve();
        };
        const timer = setTimeout(finish, timeoutMs);
        video.addEventListener("playing", finish);
        video.addEventListener("canplay", finish);
    });
}

// --- Component Definition ---
const CustomVoiceInterviewV2 = forwardRef<VideoInterviewRef, CustomVoiceInterviewV2Props>(
    (
        {
            // Destructure props
            interviewId,
            pipecatApiUrl,
            agentName,
            authToken,
            interviewContext,
            closingStatement,
            interviewerPersona,
            toneAndCommunication,
            interviewFlowPrinciples,
            informationToShare,
            s3BucketName,
            s3BucketRegion,
            awsAssumeRoleArn,
            fastapiURL,
            interviewName,
            customGreeting,
            tavusCallbacks,
            onCloseModal,
            userId,
            userCV,
            userName,
            isSkipInterview = false,
            isLoadingSkipInterview = false,
            onSkipInterview,
            showSkipLaterButton = false,
            onCompletedInterview,
            sections,
            description,
            maxDuration,
            aiName = "ourteam AI",
            aiLogoSrc,
            isTestInterview = false,
            requireSignIn = false, // Whether to require user sign-in before starting the interview
            userEmail = "",
            concludingStatements = "Thank you for your time and thoughtful responses. Have a great day!",
            job_id = null,
            company_id,
            language = "English",
            isPracticeInterview = false, // Whether this is a practice interview,
            interviewLanguage = "en-US",
            voice = "male",
            voiceKey,
            conversationIdOverride,
            sessionMode = "interview",
            leavePromptText,
            expiredBodyText,
            idleTimeoutMessage,
            isDisabledButtonIssue = false,
            handleEndSession,
            onReload,
            onInterviewStatusChange = () => { },
            hasFormAssessment = false,
            hasEligibilityCheck = false,
            actionLabel,
            loadingActionLabel,
            retakeLabel,
            tipText,
            privacyLine1,
            privacyLine2,
            headlineReady,
            expiredHeadline,
            readinessBullets,
            permissionPrompt,
            waitingForAiText,
        },
        ref: ForwardedRef<VideoInterviewRef>
    ) => {
        const { jobid } = useParams() || null;
        const snackbar = useSnackbar();

        const isMalay = interviewLanguage === "Malay";
        const isIndonesian = interviewLanguage === "id-ID";

        const t = (en: string | React.ReactNode, id: string | React.ReactNode, ms: string | React.ReactNode) => {
            if (isMalay) return ms;
            if (isIndonesian) return id;
            return en;
        };
        const [, companyInfo] = useCompanySubdomain();
        // --- State ---
        // Internet connection quality: "good" or "poor"
        const [connectionQuality, setConnectionQuality] = useState<'good' | 'poor'>('good');
        // Tracked separately from connectionQuality because "offline" is a
        // hard block (Start Interview must be disabled), while "poor" speed
        // is just a warning.
        const [isOnline, setIsOnline] = useState<boolean>(typeof navigator !== "undefined" ? navigator.onLine : true);
        // --- Internet Connection Quality Check ---
        useEffect(() => {
            // The Network Information API (`navigator.connection`) is
            // notoriously unreliable as a bandwidth signal:
            //   - `downlink` is a quantized RTT-based estimate (multiples
            //     of 0.025 Mbps, capped at 10 Mbps for anti-fingerprinting),
            //     not actual throughput. During quiet periods it routinely
            //     reports values like 0.3-1 Mbps on healthy gigabit links.
            //   - Behaviour varies by browser; Firefox barely implements it.
            // Treat `effectiveType` as the primary signal — it's based on a
            // recent observed RTT/throughput bucket and rarely false-flags
            // a working broadband link. Only escalate `downlink` to a warning
            // when it's extremely low (< 0.5 Mbps), which is below what an
            // AI voice interview needs (~150-300 kbps for Opus + WebRTC).
            const updateConnectionQuality = () => {
                setIsOnline(navigator.onLine);
                // @ts-expect-error Navigator connection API not in standard types
                const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
                if (!connection) {
                    // No Network Information API — assume good as long as the
                    // device reports online. The offline branch is handled
                    // separately via `isOnline`.
                    setConnectionQuality(navigator.onLine ? 'good' : 'poor');
                    return;
                }
                const effectiveType: string | undefined = connection.effectiveType;
                const downlink: number | undefined = typeof connection.downlink === 'number' ? connection.downlink : undefined;

                // Hard "poor" buckets from effectiveType — slow-2g/2g cannot
                // sustain real-time audio. 3g is borderline so we trust the
                // user's actual experience instead of flipping the warning.
                if (effectiveType === 'slow-2g' || effectiveType === '2g') {
                    setConnectionQuality('poor');
                    return;
                }

                // Only fall back to downlink threshold when effectiveType is
                // missing AND the reported value is implausibly low. Above
                // this floor we err on the side of "good" — false negatives
                // (poor warning on healthy link) are worse than missing a
                // genuinely weak link, since the call will surface its own
                // quality issues at runtime.
                if (!effectiveType && downlink !== undefined && downlink < 0.5) {
                    setConnectionQuality('poor');
                    return;
                }

                setConnectionQuality('good');
            };

            updateConnectionQuality();

            // Listen to changes in connection
            // @ts-expect-error Navigator connection API not in standard types
            const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
            if (connection && connection.addEventListener) {
                connection.addEventListener('change', updateConnectionQuality);
            }
            window.addEventListener('online', updateConnectionQuality);
            window.addEventListener('offline', updateConnectionQuality);

            return () => {
                if (connection && connection.removeEventListener) {
                    connection.removeEventListener('change', updateConnectionQuality);
                }
                window.removeEventListener('online', updateConnectionQuality);
                window.removeEventListener('offline', updateConnectionQuality);
            };
        }, []);

        const [status, setStatus] = useState<VideoInterviewStatus>("idle");
        const [conversationId, setConversationId] = useState<string | null>(null);
        // Mirror of `conversationId` that is NOT cleared by the
        // setTimeout(...500) cleanup at the end of endConversation. The state
        // can race to null before the BE submission calls fire (especially on
        // duplicate end triggers); reading from this ref guarantees the last
        // known non-null id ends up in /interviews/v2/update-status and
        // /assessments/v2/submit payloads.
        const conversationIdRef = useRef<string | null>(null);
        const [conversationUrl, setConversationUrl] = useState<string | null>(null);
        const [conversationToken, setConversationToken] = useState<string | null>(null);
        const [isConversationEnded, setIsConversationEnded] = useState<boolean>(false);
        const [interviewTimeDuration, setInterviewTimeDuration] = useState<number>(0); // Track interview duration
        const [, setCurrentInterviewStatus] = useState<string>(''); // Track user interview status
        const [internalErrorMessage, setInternalErrorMessage] = useState<string | React.ReactNode>("");
        const [countdownSeconds, setCountdownSeconds] = useState<number>(30); // Add countdown state
        // When the BE `createInterview` registration step fails the rest of
        // the flow (Pipecat conversation creation, Daily join) should NOT
        // proceed; we surface a dedicated retry UI instead.
        const [createInterviewError, setCreateInterviewError] = useState<string | null>(null);
        const [isRetryingCreateInterview, setIsRetryingCreateInterview] = useState<boolean>(false);

        const primaryColorClasses = {
            bg: 'bg-blue-600',
            bgHover: 'hover:bg-blue-700',
            bgLight: 'bg-blue-50',
            bgLightDark: 'dark:bg-blue-900/30',
            border: 'border-blue-100',
            borderDark: 'dark:border-blue-800',
            text: 'text-blue-600',
            textDark: 'dark:text-blue-400',
            gradient: 'bg-gradient-to-r from-blue-400 to-blue-500',
            ring: 'border-blue-400',
            ringLight: 'border-blue-300',
            ringLighter: 'border-blue-200'
        };

        // Media State
        const [micAccessGranted, setMicAccessGranted] = useState(false);
        const [cameraAccessGranted, setCameraAccessGranted] = useState(false);
        // Becomes true once the initial getUserMedia probe finishes (success or fail).
        // Until then we don't know whether permission is granted, so we should not
        // render permission-warning UI based on the default-false flags above.
        const [, setPermissionResolved] = useState(false);
        const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
        const [mediaError, setMediaError] = useState<string | null>(null);

        // UI State
        const [showMobileWarning, setShowMobileWarning] = useState(true);
        const [showConfirmationModal, setShowConfirmationModal] = useState(false);
        const [showVideoUI, setShowVideoUI] = useState(false); // Determines if waiting room or main interview UI is shown
        const [AiJoined, setAiJoined] = useState(false); // AI participant joined Daily call 
        const [isLoadingJoin, setLoadingJoin] = useState<boolean>(false);
        const [dailyJoined, setDailyJoined] = useState(false); // Local participant joined Daily call
        const [isConcluding, setIsConcluding] = useState(false);
        const [isMicDropdownOpen, setIsMicDropdownOpen] = useState(false);

        // Pre-interview audio gate state. `waitingRoomAudioStatus` is reported by
        // the always-visible AudioLevelIndicator below the video preview. The gate
        // modal variant is set when the candidate clicks Start Interview with
        // weak ("soft") or no ("hard") audio.
        const [waitingRoomAudioStatus, setWaitingRoomAudioStatus] = useState<AudioLevelStatus>("idle");
        const [audioGateVariant, setAudioGateVariant] = useState<"soft" | "hard" | null>(null);

        // Device Management State
        const [audioInputDevices, setAudioInputDevices] = useState<MediaDeviceInfo[]>([]);
        const [audioOutputDevices, setAudioOutputDevices] = useState<MediaDeviceInfo[]>([]);
        const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
        const [selectedAudioInputId, setSelectedAudioInputId] = useState<string>("");
        const [selectedAudioOutputId, setSelectedAudioOutputId] = useState<string>("");
        const [selectedVideoId, setSelectedVideoId] = useState<string>("");

        // Interview Flow State
        const [blockInterview, setBlockInterview] = useState<boolean>(false); // Blocks starting if status check fails (e.g., refresh)
        const [interviewExpired, setInterviewExpired] = useState<boolean>(false); // Countdown timed out
        const [isRetakeDialogOpen, setIsRetakeDialogOpen] = useState<boolean>(false);
        const [isIssuesDialogOpen, setIsIssuesDialogOpen] = useState<boolean>(false);
        // Tracks where the IssuesDialog was triggered from — passed through
        // to the dialog so the bug report meta records the report context.
        // - "waiting-room" → the "I'm having issues" button on the waiting room
        // - "live-session" → opened from inside the active VideoContainer
        const [reportTypeIn, setReportTypeIn] = useState<"waiting-room" | "live-session">("waiting-room");
        const [isDialogConfirmEndInterview, setIsDialogConfirmEndInterview] = useState<boolean>(false);
        const [requestSubmitted, setRequestSubmitted] = useState<boolean>(false); // Retake/Issue submitted
        const [requestType, setRequestType] = useState<"retake" | "issue" | null>(null);
        const [countdownActive, setCountdownActive] = useState<boolean>(false); // Timer started
        const [, setInterviewStatus] = useState<string | null>(null); // Initial status
        const [interviewStep, setInterviewStep] = useState<number>(0);
        const [isRecordingStarting, setIsRecordingStarting] = useState<boolean>(false); // Recording state
        const [interviewStartTime, setInterviewStartTime] = useState<number>(0);
        const [isOpenDialogMic, setIsOpenDialogMic] = useState<boolean>(false);
        const [isOpenDialogCamera, setIsOpenDialogCamera] = useState<boolean>(false);
        const [isOpenDialogSpeaker, setIsOpenDialogSpeaker] = useState<boolean>(false);
        // True while a camera switch is in flight (old track stopped → new stream
        // acquired → preview playing). Disables the camera selectors so rapid
        // back-to-back switches can't race setupMediaStream and leave a black preview.
        const [cameraSwitching, setCameraSwitching] = useState<boolean>(false);

        // --- Refs ---
        const videoRef = useRef<HTMLVideoElement>(null);
        // Synchronous mirror of cameraSwitching — guards handleCameraSelect against a
        // second invocation firing before the disabled state has re-rendered.
        const cameraSwitchingRef = useRef<boolean>(false);
        const audioRef = useRef<HTMLAudioElement>(null); // Ref for DailyAudio
        const conversationCreatedRef = useRef<boolean>(false); // Prevent multiple creation attempts
        const mediaStreamRef = useRef<MediaStream | null>(null); // Ref to track mediaStream for cleanup
        const callObjectRef = useRef<ReturnType<typeof useDaily>>(null); // Ref to track callObject for cleanup
        const isMountedRef = useRef<boolean>(true); // Track if component is still mounted
        // Refs that mirror selection state — read from the devicechange handler
        // (registered once via [] deps) to avoid stale-closure reads.
        const selectedAudioInputIdRef = useRef<string>("");
        const selectedVideoIdRef = useRef<string>("");
        const setupMediaStreamRef = useRef<((audioId?: string, videoId?: string) => Promise<MediaStream | undefined | void>) | null>(null);
        const [, setIsAiSpeaking] = useState(false);

        // Keep mediaStreamRef in sync with mediaStream state
        useEffect(() => {
            mediaStreamRef.current = mediaStream;
        }, [mediaStream]);

        // Sync selection refs (used by once-registered devicechange handler).
        useEffect(() => {
            selectedAudioInputIdRef.current = selectedAudioInputId;
        }, [selectedAudioInputId]);
        useEffect(() => {
            selectedVideoIdRef.current = selectedVideoId;
        }, [selectedVideoId]);

        const callObject = useDaily();

        // Keep callObjectRef in sync with callObject
        useEffect(() => {
            callObjectRef.current = callObject;
        }, [callObject]);

        const remoteParticipantIds = useParticipantIds({ filter: "remote" }); // <-- Get remote participants

        // Calculate max duration from sections if not provided
        const maxDurationInterview = (sections || []).map((section) => section.duration || 0).reduce((a, b) => a + b, 0) || maxDuration;

        const [isBrowserSupported, setIsBrowserSupported] = useState<boolean>(true);

        // Calculate dynamic thresholds based on maxDuration prop
        const { minDuration25Percent, minDuration50Percent } = useMemo(() => {
            const targetDuration = maxDuration || 1200; // Default to 20 minutes if not provided
            return {
                minDuration25Percent: targetDuration * 0.25, // 25% threshold
                minDuration50Percent: targetDuration * 0.50, // 50% threshold
            };
        }, [maxDurationInterview]);

        const interviewContexts = React.useMemo<string[]>(() => {
            console.log("Generating interview context instructions for sections:", JSON.stringify(sections));
            return sections.map((section) => (`Current Section Instructions:\n${section?.instruction}\n\n${(section.questions || []).length > 0 ? `### MANDATORY QUESTIONS ###\n\nYou MUST ask these questions in order (but make it natural):\n\n${(section?.questions || []).map((q, idx) => { return `${idx + 1}. ${q.text}\n${(q?.followUpInstructions || []).length > 0 ? `Follow up instructions: ${(q?.followUpInstructions || []).join('; ')}` : ''}`; }).join('\n')}` : ""}`))
        }, [sections]);

        const detectBrowser = () => {
            const { name } = getBrowserName();

            console.log("Browser detection:", {
                name,
                isSupported: name === "Chrome" || name === "Firefox" || name === "Safari" || name === "Edge" || name === "Mozilla"
            });

            return name === "Chrome" || name === "Firefox" || name === "Safari" || name === "Edge" || name === "Mozilla";
        };

        // Check browser compatibility on mount
        useEffect(() => {
            setIsBrowserSupported(detectBrowser());
        }, []);

        // Identify the AI participant (assuming it's the first remote participant)
        const aiParticipantId = useMemo(() => {
            // You might need a more robust way to identify the AI if multiple remotes are possible
            return remoteParticipantIds.length > 0 ? remoteParticipantIds[0] : null;
        }, [remoteParticipantIds]);

        // --- Constants ---
        const timeoutMinutes = 3; // Countdown duration
        const { updateReceiveSettings } = useReceiveSettings()
        const { updateSendSettings } = useSendSettings()

        // -- Talking Indicator --
        const { isTalking, speech_status, error, startListening, stopListening, audioStatus, getAudioStats } = useSpeechDetection();

        useEffect(() => {
            if (showVideoUI && status === 'active') {
                const intervalId = setInterval(() => {
                    setInterviewTimeDuration((prev) => prev + 1);
                }, 1000)

                return () => clearInterval(intervalId);
            }
        }, [showVideoUI, status])

        // Separate effect to update interview status based on duration
        useEffect(() => {
            let userInterviewStatus: string = 'Complete';

            // interview time duration < 25% of target duration
            if (interviewTimeDuration < minDuration25Percent) {
                userInterviewStatus = 'Aborted';
            }
            // interview time duration >= 25% and <= 50% of target duration
            else if (interviewTimeDuration >= minDuration25Percent && interviewTimeDuration <= minDuration50Percent) {
                userInterviewStatus = 'Partial';
            }

            setCurrentInterviewStatus(userInterviewStatus);
        }, [interviewTimeDuration, minDuration25Percent, minDuration50Percent])

        useEffect(() => {
            console.log("Current Speech Status:", speech_status, audioStatus); // Log status changes
            if (error) {
                console.error("Speech Detection Error:", error); // Log errors
            }
        }, [speech_status, error, audioStatus]);

        useEffect(() => {
            let intervalId: NodeJS.Timeout | null = null;

            if (isConcluding) {
                // Start the countdown timer
                intervalId = setInterval(() => {
                    setCountdownSeconds((prevSeconds) => {
                        if (prevSeconds <= 1) {
                            // If countdown reaches 0 or less, clear the interval
                            console.log("timeout")
                            endConversation('end')
                            if (!isTestInterview) {
                                let userInterviewStatus: string = 'Complete';
                                // interview time duration < 25% of target duration
                                if (interviewTimeDuration < minDuration25Percent) {
                                    userInterviewStatus = 'Aborted';
                                }
                                // interview time duration >= 25% and <= 50% of target duration
                                else if (interviewTimeDuration >= minDuration25Percent && interviewTimeDuration <= minDuration50Percent) {
                                    userInterviewStatus = 'Partial';
                                }

                                updateInterviewStatusSafe({ new_status: userInterviewStatus, conversation_id: (conversationIdRef.current ?? conversationId) as string }).then(() => {
                                    if (onInterviewStatusChange) {
                                        onInterviewStatusChange(userInterviewStatus);
                                    }
                                    submitAssessmentV2({
                                        assessment_id: interviewId,
                                        answers: [{ id: "conversation_id", content: (conversationIdRef.current ?? conversationId) as string }],
                                        job_id: job_id as string,
                                        submission_type: "Interview"
                                    }).catch((err) => {
                                        console.error("Failed to submit assessment:", err);
                                        catchErrorLog(`Failed to submit assessment. Err: ${err}`);
                                    });
                                    if (userInterviewStatus !== 'Aborted') {
                                        console.log(`[Timeout] Interview status: ${userInterviewStatus}, calling onCompletedInterview`);
                                        onCompletedInterview();
                                    } else {
                                        console.log(`[Timeout] Interview status: ${userInterviewStatus}, skipping onCompletedInterview`);
                                    }
                                })
                            }
                            setStatus('completed');
                            if (intervalId) {
                                clearInterval(intervalId);
                            }
                            return 0; // Keep it at 0
                        }
                        return prevSeconds - 1; // Decrement
                    });
                }, 1000); // Run every 1 second
            } else {
                // If not concluding, ensure timer is cleared (cleanup also does this)
                if (intervalId) {
                    clearInterval(intervalId);
                }
                // Optionally reset countdownSeconds when concluding stops if needed
                // setCountdownSeconds(30); // Reset to initial value
            }

            // Cleanup function: Clear the interval when the component unmounts
            // or when isConcluding changes before the interval finishes
            return () => {
                if (intervalId) {
                    clearInterval(intervalId);
                }
            };
        }, [isConcluding]); // Re-run effect if isConcluding, interviewTimeDuration, or currentInterviewStatus changes

        useEffect(() => {
            // Ensure callObject and the AI participant ID are available
            if (callObject && aiParticipantId) {
                console.log(
                    `VideoContainer: Requesting low quality video layer for AI participant (${aiParticipantId})`
                );
                updateReceiveSettings({
                    // Target the specific AI participant using their session ID
                    [aiParticipantId]: {
                        video: {
                            // Request the lowest quality video layer (0)
                            // 0 = low, 1 = medium, 2 = high (if available)
                            // This relies on the sender (AI) using simulcast.
                            layer: 0,

                            // Optional: You could also set 'subscribe: true/false' here
                            // if you wanted to completely turn the video track on/off,
                            // but 'layer: 0' is better for reducing quality while keeping video.
                        },
                        // You can also set settings for 'audio' or 'screenVideo' here if needed
                    },
                    // You could set global defaults using '*': {} , but we want specific control
                });
                console.log(
                    `VideoContainer: Requesting low quality video layer for user participant (${aiParticipantId})`
                );
                updateSendSettings({
                    video: 'bandwidth-optimized',
                }

                )
            }
        }, [callObject, aiParticipantId]); // Re-run this effect if the callObject or AI participant ID changes


        // --- Callbacks & Handlers ---

        const handleAudioPlayFailed = useCallback((e: { type?: string; sessionId?: string }) => {
            console.error(
                `Failed to play ${e.type} audio for ${e.sessionId || "unknown participant"
                }.`,
                e
            );
            // Optional: Update UI to show audio playback error
            setInternalErrorMessage(
                "Audio playback failed. Check speaker settings or browser permissions."
            );
        }, []);

        const setupMediaStream = useCallback(
            async (
                audioDeviceId: string = selectedAudioInputId, // Default to current selection
                videoDeviceId: string = selectedVideoId // Default to current selection
            ) => {
                console.log(
                    `Attempting to setup media stream with audio: ${audioDeviceId}, video: ${videoDeviceId}`
                );

                // Define constraints variable *before* the try block
                let constraints: MediaStreamConstraints = {}; // Initialize

                // Stop any existing tracks before requesting new ones
                if (mediaStream) {
                    console.log("Stopping previous media tracks");
                    mediaStream.getTracks().forEach((track) => {
                        track.stop();
                        console.log(`Stopped ${track.kind} track: ${track.label}`);
                    });
                    setMediaStream(null); // Clear the state immediately
                    if (videoRef.current) {
                        videoRef.current.srcObject = null; // Clear video element source
                    }
                }

                // Check if device IDs are actually available (might be empty initially)
                if (!audioDeviceId && audioInputDevices.length > 0) {
                    console.warn("No specific audio input ID, but devices exist. Will use default.");
                    audioDeviceId = audioInputDevices[0].deviceId; // Fallback might be needed
                }
                if (!videoDeviceId && videoDevices.length > 0) {
                    console.warn("No specific video input ID, but devices exist. Will use default.");
                    videoDeviceId = videoDevices[0].deviceId; // Fallback might be needed
                }

                try {
                    // Assign constraints inside the try block (or re-assign if needed)
                    constraints = {
                        video: videoDeviceId ? { deviceId: { exact: videoDeviceId } } : true,
                    };

                    console.log("Requesting media with constraints:", constraints);
                    // Android frees the camera hardware ASYNCHRONOUSLY after the old
                    // track.stop() above, so immediately re-acquiring the *other* camera
                    // on a front↔back switch often throws NotReadableError ("Could not
                    // start video source") while the previous camera is still releasing.
                    // The switch then silently fails and the preview goes blank — which
                    // is exactly why device switching breaks on Android but not iOS
                    // (iOS releases the camera synchronously on stop()). Retry a couple
                    // of times with a short backoff so the switch lands on Android; on
                    // iOS the first attempt succeeds, so this is a no-op there.
                    const acquireStream = async (): Promise<MediaStream> => {
                        for (let attempt = 0; ; attempt++) {
                            try {
                                return await navigator.mediaDevices.getUserMedia(constraints);
                            } catch (gumErr) {
                                if (
                                    gumErr instanceof Error &&
                                    gumErr.name === "NotReadableError" &&
                                    attempt < 2
                                ) {
                                    console.warn(
                                        `Camera still releasing (NotReadableError); retry #${attempt + 1} in ${200 * (attempt + 1)}ms`,
                                    );
                                    await new Promise((resolve) => setTimeout(resolve, 200 * (attempt + 1)));
                                    continue;
                                }
                                throw gumErr;
                            }
                        }
                    };
                    const stream = await acquireStream();
                    console.log("Stream obtained successfully:", stream.id);
                    console.log("Video tracks:", stream.getVideoTracks().map((t) => `${t.label} (${t.id})`));

                    // If component unmounted while awaiting getUserMedia, stop the stream immediately
                    if (!isMountedRef.current) {
                        console.log("Component unmounted during getUserMedia, stopping orphaned stream");
                        stream.getTracks().forEach(track => track.stop());
                        return;
                    }

                    setMediaStream(stream); // Update state
                    setCameraAccessGranted(true);
                    setMediaError(null);

                    // Attach stream to video element
                    if (videoRef.current) {
                        console.log("Attaching stream to video element");
                        videoRef.current.srcObject = stream;
                        await videoRef.current.play().catch((err) => {
                            console.error("Error playing video preview:", err);
                            // It might still work, but log the error. Autoplay might be blocked.
                        });
                    } else {
                        console.warn("Video ref is not available for preview.");
                    }

                    // Apply audio output selection if supported
                    if (
                        selectedAudioOutputId &&
                        audioRef.current &&
                        "setSinkId" in HTMLMediaElement.prototype
                    ) {
                        try {
                            const audioEl = (audioRef.current as HTMLElement).querySelector?.("audio") || audioRef.current;
                            console.log("Setting audio output device:", selectedAudioOutputId);
                            if (typeof (audioEl as any).setSinkId === 'function') {
                                await (audioEl as HTMLMediaElement & { setSinkId: (id: string) => Promise<void> }).setSinkId(selectedAudioOutputId);
                            } else {
                                console.warn('setSinkId not supported on this audio element');
                            }
                        } catch (err) {
                            console.error("Error setting audio output device:", err);
                            setInternalErrorMessage("Failed to set speaker device.");
                        }
                    }
                    return stream; // Return the stream for potential immediate use elsewhere
                } catch (err) {
                    console.error("Error accessing media devices:", err);
                    let errorMessage = "Could not access camera or microphone.";
                    if (err instanceof Error) {
                        if (err.name === "NotAllowedError") {
                            errorMessage =
                                "Permission denied. Please grant access to your camera and microphone in browser settings.";
                        } else if (err.name === "NotFoundError") {
                            errorMessage =
                                "No camera or microphone found. Please check connections.";
                        } else if (err.name === "OverconstrainedError") {
                            errorMessage =
                                "Selected device unavailable. Choose another or ensure it's not in use.";
                            // Reset selection if the specific device caused the error
                            // Now 'constraints' is accessible here
                            if ((err as OverconstrainedError).constraint === 'deviceId' && constraints) {
                                if (constraints.audio && typeof constraints.audio === 'object' && constraints.audio.deviceId === audioDeviceId) {
                                    console.warn("Resetting failed audio input selection due to OverconstrainedError");
                                    setSelectedAudioInputId(''); // Reset to default
                                }
                                if (constraints.video && typeof constraints.video === 'object' && constraints.video.deviceId === videoDeviceId) {
                                    console.warn("Resetting failed video input selection due to OverconstrainedError");
                                    setSelectedVideoId(''); // Reset to default
                                }
                            }
                        } else {
                            errorMessage = `Media Error: ${err.message}`;
                        }
                    }
                    setMediaError(errorMessage);
                    // Only treat permission denial as "not granted". Transient
                    // errors (NotReadableError, OverconstrainedError, NotFoundError)
                    // are device-level issues, not permission revocations — leave
                    // cameraAccessGranted alone so the UI doesn't flicker the
                    // permission warning on harmless device hiccups.
                    if (err instanceof Error && err.name === "NotAllowedError") {
                        setCameraAccessGranted(false);
                    }
                    setMediaStream(null);
                    if (videoRef.current) {
                        videoRef.current.srcObject = null;
                    }
                }
            },
            [mediaStream, selectedAudioInputId, selectedVideoId, selectedAudioOutputId, audioInputDevices, videoDevices] // Dependencies needed for correct closure
        );

        // Mirror the latest setupMediaStream into a ref so the once-registered
        // devicechange handler always calls the current implementation.
        useEffect(() => {
            setupMediaStreamRef.current = setupMediaStream;
        }, [setupMediaStream]);

        // Effect for Initial Device Enumeration and Stream Setup (Waiting Room Only)
        useEffect(() => {
            // Only run this setup when in the waiting room UI
            if (showVideoUI) return;

            let isMounted = true; // Flag to prevent state updates after unmount
            // Store video ref at mount time for cleanup (to satisfy React lint rules)
            const currentVideoRefAtMount = videoRef.current;

            const enumerateAndSetup = async () => {
                console.log("Effect: Enumerating devices and setting up initial stream...");
                try {
                    // Request dummy stream first to ensure permissions are granted *before* enumeration
                    // This helps get accurate device labels.
                    // Track all temp streams to ensure proper cleanup
                    const tempStreamsToCleanup: MediaStream[] = [];
                    // The video probe stream is held aside and reused as the
                    // initial preview stream — see comment near the reuse
                    // block below for the macOS FaceTime HD rationale.
                    let initialVideoStream: MediaStream | null = null;

                    try {
                        const videoTempStream = await navigator.mediaDevices.getUserMedia({ video: true });
                        initialVideoStream = videoTempStream;
                        setCameraAccessGranted(true);
                        console.warn("Video permission granted.");
                    } catch (permError) {
                        console.error("Initial permission request for camera failed:", permError);
                        // Set error state based on permission denial
                        if (permError instanceof Error && permError.name === 'NotAllowedError') {
                            setMediaError("Permission denied. Please grant access to camera.");
                        } else {
                            setMediaError("Could not access camera. Check connections/permissions.");
                        }
                        setCameraAccessGranted(false);
                        // Still try to enumerate, might get devices without labels
                    }

                    try {
                        const audioTempStream = await navigator.mediaDevices.getUserMedia({ audio: true });
                        tempStreamsToCleanup.push(audioTempStream);
                        setMicAccessGranted(true);
                        console.warn("Mic permission granted.");
                    } catch (permError) {
                        console.error("Initial permission request for mic failed:", permError);
                        // Set error state based on permission denial
                        if (permError instanceof Error && permError.name === 'NotAllowedError') {
                            setMediaError("Permission denied. Please grant access to microphone.");
                        } else {
                            setMediaError("Could not access microphone. Check connections/permissions.");
                        }

                        setMicAccessGranted(false);
                        // Still try to enumerate, might get devices without labels
                    }

                    // Probes are done — render permission-aware UI from here on.
                    if (isMounted) setPermissionResolved(true);


                    // Now enumerate devices
                    const devices = await navigator.mediaDevices.enumerateDevices();
                    console.log("Enumerated devices:", devices);

                    const audioInputs = devices.filter(
                        (d) => d.kind === "audioinput" && d.deviceId !== "default" && d.deviceId !== 'communications' && d.deviceId !== ""
                    );
                    const audioOutputs = devices.filter((d) => d.kind === "audiooutput" && d.deviceId !== "");
                    const videoInputs = devices.filter((d) => d.kind === "videoinput" && d.deviceId !== "");

                    if (!isMounted) {
                        // Component already unmounted while we were awaiting
                        // enumeration — release the held probe stream so we
                        // do not leak the camera.
                        initialVideoStream?.getTracks().forEach((t) => t.stop());
                        tempStreamsToCleanup.forEach((s) => s.getTracks().forEach((t) => t.stop()));
                        return;
                    }

                    setAudioInputDevices(audioInputs);
                    setAudioOutputDevices(audioOutputs);
                    setVideoDevices(videoInputs);

                    // Stop ALL temporary streams *after* enumeration
                    tempStreamsToCleanup.forEach(stream => {
                        stream.getTracks().forEach(track => {
                            console.log(`Stopping temp stream track: ${track.kind} (${track.label})`);
                            track.stop();
                        });
                    });

                    // Determine initial device selections (use first available if no selection yet)
                    const initialAudioInputId = selectedAudioInputId || (audioInputs.length > 0 ? audioInputs[0].deviceId : '');
                    const initialVideoId = selectedVideoId || (videoInputs.length > 0 ? videoInputs[0].deviceId : '');
                    const initialAudioOutputId = selectedAudioOutputId || (audioOutputs.length > 0 ? audioOutputs[0].deviceId : '');

                    // Update state only if changed
                    if (initialAudioInputId !== selectedAudioInputId) setSelectedAudioInputId(initialAudioInputId);
                    if (initialVideoId !== selectedVideoId) setSelectedVideoId(initialVideoId);
                    if (initialAudioOutputId !== selectedAudioOutputId) setSelectedAudioOutputId(initialAudioOutputId);


                    // Reuse the video probe stream as the initial preview rather
                    // than stopping it and re-acquiring via setupMediaStream. On
                    // macOS the built-in FaceTime HD camera does not release
                    // cleanly between back-to-back getUserMedia calls — the
                    // second track resolves but stays muted/no-frames for
                    // several seconds, which is exactly the "blank gray box"
                    // bug we hit only on built-in laptop cameras. External /
                    // virtual cameras are unaffected because their handover
                    // path is different. Reusing the probe stream sidesteps
                    // the stop+restart entirely on initial load.
                    if (initialVideoStream && isMounted) {
                        console.log("Reusing video probe stream as initial preview:",
                            initialVideoStream.getVideoTracks().map((t) => `${t.label} (${t.id})`));
                        setMediaStream(initialVideoStream);
                        setMediaError(null);
                        if (videoRef.current) {
                            videoRef.current.srcObject = initialVideoStream;
                            videoRef.current.play().catch((err) => {
                                console.error("Error playing initial video preview:", err);
                            });
                        }
                    } else if (audioInputs.length > 0 && videoInputs.length > 0) {
                        // Fallback when the probe stream is unavailable (e.g.
                        // permission was granted in a previous session and
                        // the probe was skipped, or it failed): go through
                        // the regular acquisition path.
                        await setupMediaStream(initialAudioInputId, initialVideoId);
                    }


                    // Add device change listener
                    navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange);

                } catch (error) {
                    console.error("Error during initial device setup:", error);
                    if (isMounted) {
                        setMediaError(
                            "Error accessing media devices. Check permissions/connections."
                        );
                    }
                }
            };

            // Handler for device changes *after* initial setup
            const handleDeviceChange = async () => {
                if (!isMounted || showVideoUI) return; // Don't run if not mounted or in main interview
                console.log("Device change detected. Re-enumerating...");

                // Re-enumerate to get updated list. Match the strict initial filter
                // (also drop empty deviceIds) so the existence checks below don't see
                // synthetic placeholder entries that the original selection never had.
                const devices = await navigator.mediaDevices.enumerateDevices();
                const audioInputs = devices.filter(
                    (d) => d.kind === "audioinput" && d.deviceId !== "default" && d.deviceId !== 'communications' && d.deviceId !== ""
                );
                const audioOutputs = devices.filter((d) => d.kind === "audiooutput" && d.deviceId !== "");
                const videoInputs = devices.filter((d) => d.kind === "videoinput" && d.deviceId !== "");

                if (!isMounted) return;

                setAudioInputDevices(audioInputs);
                setAudioOutputDevices(audioOutputs);
                setVideoDevices(videoInputs);

                // Read current selections from refs to avoid the once-registered
                // listener seeing stale values from initial render.
                const currentAudioInputId = selectedAudioInputIdRef.current;
                const currentVideoId = selectedVideoIdRef.current;

                // Check if the *currently selected* devices still exist. We only
                // care if a non-empty selection has been lost — empty means we
                // never picked anything, so nothing was "lost".
                const audioInputStillExists = currentAudioInputId !== "" && audioInputs.some(
                    (d) => d.deviceId === currentAudioInputId
                );
                const videoStillExists = currentVideoId !== "" && videoInputs.some(
                    (d) => d.deviceId === currentVideoId
                );

                let newAudioId = currentAudioInputId;
                let newVideoId = currentVideoId;
                let streamNeedsUpdate = false;

                if (currentAudioInputId !== "" && !audioInputStillExists && audioInputs.length > 0) {
                    console.warn("Selected audio input lost. Selecting new default.");
                    newAudioId = audioInputs[0].deviceId;
                    setSelectedAudioInputId(newAudioId); // Update state
                    streamNeedsUpdate = true;
                } else if (currentAudioInputId !== "" && !audioInputStillExists && audioInputs.length === 0) {
                    console.error("All audio input devices lost.");
                    setMicAccessGranted(false);
                    setMediaError("Microphone disconnected.");
                    setSelectedAudioInputId('');
                    streamNeedsUpdate = true; // Attempt to stop the old stream
                }

                if (currentVideoId !== "" && !videoStillExists && videoInputs.length > 0) {
                    console.warn("Selected video input lost. Selecting new default.");
                    newVideoId = videoInputs[0].deviceId;
                    setSelectedVideoId(newVideoId); // Update state
                    streamNeedsUpdate = true;
                } else if (currentVideoId !== "" && !videoStillExists && videoInputs.length === 0) {
                    console.error("All video input devices lost.");
                    setCameraAccessGranted(false);
                    setMediaError("Camera disconnected.");
                    setSelectedVideoId('');
                    streamNeedsUpdate = true; // Attempt to stop the old stream
                }

                // Check audio output
                const audioOutputStillExists = audioOutputs.some(
                    (d) => d.deviceId === selectedAudioOutputId
                );
                if (!audioOutputStillExists && audioOutputs.length > 0) {
                    console.warn("Selected audio output lost. Selecting new default.");
                    setSelectedAudioOutputId(audioOutputs[0].deviceId);
                    // No stream update needed for output, just apply sinkId if possible later
                } else if (!audioOutputStillExists && audioOutputs.length === 0) {
                    console.warn("All audio output devices lost.");
                    setSelectedAudioOutputId('');
                }


                if (streamNeedsUpdate) {
                    console.log("Device change necessitates stream update.");
                    // Call the latest setupMediaStream via ref so we don't
                    // execute a stale closure captured at mount time.
                    await setupMediaStreamRef.current?.(newAudioId, newVideoId);
                }
            };


            enumerateAndSetup();

            // Cleanup function
            return () => {
                isMounted = false;
                console.log("Effect Cleanup: Media setup", mediaStreamRef.current);
                navigator.mediaDevices.removeEventListener("devicechange", handleDeviceChange);

                // Stop media tracks on cleanup using ref (to get the latest value)
                if (mediaStreamRef.current) {
                    console.log("Cleaning up media tracks from effect");
                    mediaStreamRef.current.getTracks().forEach((track) => {
                        console.log(`Stopping track: ${track.kind} (${track.label})`);
                        track.stop();
                    });
                    mediaStreamRef.current = null;
                }
                // Clear video source using the ref captured at mount
                const videoRefToClean = currentVideoRefAtMount || videoRef.current;
                if (videoRefToClean) {
                    console.log("Clearing video source on cleanup");
                    videoRefToClean.srcObject = null;
                }
            };
        }, []); // Rerun only if we switch between waiting room and main UI

        // Local speech detection
        useEffect(() => {
            // Try starting speech detection once mic access is confirmed
            if (micAccessGranted) {
                console.log("Attempting to start local speech detection...");
                startListening().catch(err => {
                    // Handle potential errors from startListening itself if needed
                    console.error("Error calling startListening:", err);
                });
            }

            // Cleanup: Stop speech detection when component unmounts or micAccessGranted becomes false
            return () => {
                console.log("Speech detection effect cleanup: stopping listening");
                stopListening();
            };
        }, [micAccessGranted, startListening, stopListening]);

        // Device Selection Handlers
        const handleMicrophoneSelect = useCallback((device: MediaDeviceInfo) => {
            console.log(`Microphone selected: ${device.label} (${device.deviceId})`);
            setSelectedAudioInputId(device.deviceId);

            if (callObject && callObject.meetingState() === 'joined-meeting') {
                console.log("Applying microphone change to Daily call:", device.deviceId);
                callObject.setInputDevicesAsync({ audioDeviceId: device.deviceId })
                    .catch(err => console.error("Error setting Daily audio input device:", err));

                setupMediaStream(device.deviceId, selectedVideoId);
            } else {
                // Still update local preview if not in main call UI or Daily not ready
                console.log("Applying microphone change to local preview.");
                // Ensure selectedVideoId is current when calling setupMediaStream
                setupMediaStream(device.deviceId, selectedVideoId);
            }
        }, [selectedVideoId, setupMediaStream, callObject]);

        const handleSpeakerSelect = useCallback((device: MediaDeviceInfo) => {
            console.log(`Speaker selected: ${device.label} (${device.deviceId})`);
            setSelectedAudioOutputId(device.deviceId);

            if (callObject && callObject.meetingState() === 'joined-meeting') {
                console.log("Applying speaker change to Daily call:", device.deviceId);
                if ('setOutputDeviceAsync' in callObject) {
                    callObject.setOutputDeviceAsync({ outputDeviceId: device.deviceId })
                        .catch(err => console.error("Error setting Daily audio output device:", err));
                } else {
                    console.warn("callObject.setOutputDeviceAsync is not available. Speaker change might not apply to Daily call.");
                }
            } else {
                // For waiting room, apply to DailyAudio element
                if (audioRef.current && "setSinkId" in HTMLMediaElement.prototype) {
                    const audioEl = (audioRef.current as HTMLElement).querySelector?.("audio") || audioRef.current;
                    console.log("Applying speaker change to local audio element (DailyAudio).");
                    if (typeof (audioEl as any).setSinkId === 'function') {
                        (audioEl as HTMLMediaElement & { setSinkId: (id: string) => Promise<void> }).setSinkId(device.deviceId).catch((err: unknown) => {
                            console.error("Error setting audio output device on selection (local):", err);
                        });
                    } else {
                        console.warn('setSinkId not supported on this audio element');
                    }
                }
            }
        }, [callObject]);

        const handleCameraSelect = useCallback(async (device: MediaDeviceInfo) => {
            // Ignore taps while a switch is still in flight — rapid back-to-back
            // switches race setupMediaStream (one call stops the stream another just
            // attached) and leave the preview black. The selectors are also disabled
            // via `cameraSwitching`; this ref-guard covers the gap before re-render.
            if (cameraSwitchingRef.current) return;
            console.log(`Camera selected: ${device.label} (${device.deviceId})`);
            setSelectedVideoId(device.deviceId);
            cameraSwitchingRef.current = true;
            setCameraSwitching(true);

            try {
                if (callObject && callObject.meetingState() === 'joined-meeting') {
                    console.log("Applying camera change to Daily call:", device.deviceId);
                    callObject.setInputDevicesAsync({ videoDeviceId: device.deviceId })
                        .catch(err => console.error("Error setting Daily video input device:", err));
                }
                // Re-acquire the local preview with the chosen camera, then keep the
                // selectors disabled until the preview is actually rendering again.
                const stream = await setupMediaStream(selectedAudioInputId, device.deviceId);
                if (stream && videoRef.current) {
                    await waitForVideoPlaying(videoRef.current);
                }
            } catch (err) {
                console.error("Camera switch failed:", err);
            } finally {
                cameraSwitchingRef.current = false;
                setCameraSwitching(false);
            }
        }, [selectedAudioInputId, setupMediaStream, callObject]);


        // Check Interview Status (e.g., prevent joining if already completed/blocked)
        const checkInterviewStatus = useCallback(async () => {
            console.log("Checking interview status...");
            try {
                if (requireSignIn) {
                    const res = await getInterviewStatus(interviewId); // Assuming this uses userId implicitly or pass it
                    console.log("Interview Status Check Response:", res);
                    setInterviewStatus(res?.data?.status || null);
                    if (res?.data?.status === "Pending Interview") {
                        setBlockInterview(false);
                    } else {
                        // Handle specific statuses with custom messages
                        console.warn(`Interview status is not 'Pending Interview': ${res?.data?.status}`);
                        setBlockInterview(true);

                        // Set specific error messages based on status
                        if (res?.data?.status === "None") {
                            setInternalErrorMessage(<>
                                This interview session has expired. You did not join within<br />the allocated time.
                            </>);
                        } else if (res?.data?.status === "Interviewer Ready") {
                            setInternalErrorMessage("It looks like you refreshed the page, which ended the session. \nPlease request to retake.");
                        } else {
                            // Default message for other statuses
                            setInternalErrorMessage(`Interview access blocked (Status: ${res?.data?.status}). You may need to request a retake.`);
                        }
                    }
                } else {
                    setBlockInterview(false);
                    setInterviewStatus("Pending Interview"); // Default for non-authenticated interviews
                }
            } catch (error: unknown) {
                console.error("Failed to get interview status:", error);
                setBlockInterview(true); // Block if status check fails
                setInternalErrorMessage("Could not verify session status. Please try refreshing or contact support.");
            }
        }, []);

        useEffect(() => {
            // Check status once when the component mounts
            checkInterviewStatus();
        }, []);


        // Create Conversation via pipecat cloud
        const createConversation = useCallback(async () => {
            // Prevent creation if blocked, already created, or in wrong state
            if (blockInterview || conversationCreatedRef.current || (status !== 'idle' && status !== 'error')) {
                console.warn(`Skipping conversation creation. Blocked: ${blockInterview}, Created: ${conversationCreatedRef.current}, Status: ${status}`);
                return;
            }

            conversationCreatedRef.current = true; // Mark as attempting creation
            setStatus('connecting');
            setInternalErrorMessage(''); // Clear previous errors
            setCreateInterviewError(null); // Clear any prior registration failure

            console.log("API: Creating Pipecat conversation...");
            try {
                // Construct the request URL with agent name
                const requestUrl = `${pipecatApiUrl}/v1/public/${agentName}/start`;

                // Prepare request body according to Pipecat's format
                // Generate a UUID for the conversation
                let newConversationId = crypto.randomUUID();

                // When the caller provides an explicit conversation id (e.g.
                // training passes the TrainingSession.id so the BE scoring
                // pipeline can match Pipecat's transcript callback back to
                // the session), use it verbatim and skip the recruitment-side
                // `createInterview` registration step.
                if (conversationIdOverride) {
                    newConversationId = conversationIdOverride;
                } else if (!isTestInterview) {
                    const storageKey = `interview_conversation_id_${interviewId}_${userId}`;
                    const cachedConversationId = localStorage.getItem(storageKey);

                    if (cachedConversationId) {
                        newConversationId = cachedConversationId;
                        console.log("Using cached conversation ID from localStorage:", newConversationId);
                        // Log to Sentry so we can audit how often candidates
                        // resume against a cached id (vs. registering fresh)
                        // and correlate it with downstream failures.
                        catchErrorLog(
                            `Using cached conversation ID from localStorage: ${JSON.stringify({
                                where: "createConversation:cachedId",
                                conversation_id: newConversationId,
                                assessment_id: interviewId,
                                user_id: userId,
                                user_email: userEmail,
                                job_id: job_id,
                                interview_name: interviewName,
                            })}`
                        );
                    } else {
                        console.log("Creating backend interview for non-test mode", interviewId, interviewName, newConversationId);
                        const isFirstMove = interviewId === import.meta.env.VITE_FIRST_MOVE_INTERVIEW_ID;
                        const createInterviewPayload = {
                            conversation_id: newConversationId,
                            interview: interviewName,
                            assessment_id: interviewId,
                            browser_details: `${getBrowserName().name} (${getBrowserName().version})`,
                            job_id: job_id,
                            ...(isFirstMove ? { email: userEmail } : {}),
                        };

                        try {
                            const createResponse = isFirstMove
                                ? await createInterview(createInterviewPayload, import.meta.env.VITE_FIRST_MOVE_API)
                                : await createInterview(createInterviewPayload);

                            // The BE owns the conversation_id — adopt whatever it returns,
                            // on 201 and 202 alike. Keeping our local id makes every later
                            // call 404 and silently loses the interview (#3460).
                            newConversationId = resolveConversationId(createResponse, newConversationId);
                            console.log("Backend create interview response:", createResponse);
                            localStorage.setItem(storageKey, newConversationId);
                        } catch (registrationError: unknown) {
                            // createInterview failed — stop the rest of the flow
                            // (Pipecat / Daily) and surface a retry UI to the
                            // user. Capture a detailed Sentry breadcrumb so we
                            // can correlate failures with assessment/user/job.
                            const axiosErr = registrationError as {
                                message?: string;
                                response?: { status?: number; data?: unknown };
                                config?: { url?: string; method?: string };
                            };
                            const detail = {
                                where: "createInterview",
                                mode: isFirstMove ? "first_move" : "standard",
                                api_base: isFirstMove
                                    ? import.meta.env.VITE_FIRST_MOVE_API
                                    : import.meta.env.VITE_TALENT_AI_BE_BASE_URL,
                                request_url: axiosErr.config?.url ?? null,
                                request_method: axiosErr.config?.method ?? null,
                                http_status: axiosErr.response?.status ?? null,
                                response_body: axiosErr.response?.data ?? null,
                                error_message:
                                    axiosErr.message ??
                                    String(registrationError),
                                assessment_id: interviewId,
                                conversation_id: newConversationId,
                                user_id: userId,
                                user_email: userEmail,
                                job_id: job_id,
                                interview_name: interviewName,
                                browser: `${getBrowserName().name} (${getBrowserName().version})`,
                            };
                            console.error("API: createInterview failed", detail);
                            catchErrorLog(
                                `createInterview failed: ${JSON.stringify(detail)}`
                            );
                            conversationCreatedRef.current = false; // allow retry
                            // User-facing copy is intentionally generic — the
                            // technical payload (status code, response body,
                            // request URL, etc.) lives in Sentry for engineers
                            // to debug. The candidate just needs a clear
                            // next step.
                            setCreateInterviewError(
                                t(
                                    "Something went wrong while preparing your interview. Please try again in a moment.",
                                    "Terjadi kesalahan saat menyiapkan wawancara Anda. Silakan coba lagi sebentar.",
                                    "Terdapat masalah semasa menyediakan temuduga anda. Sila cuba lagi sebentar."
                                ) as string
                            );
                            setStatus("error");
                            return; // STOP — do not proceed to Pipecat
                        }
                    }
                } else {
                    console.log("Test interview - skipping backend interview creation");
                }

                // const interviewContextResp = await getInterviewContexts(company_id ?? "");

                const requestBody = {
                    createDailyRoom: true,
                    dailyRoomProperties: {
                        // enable_recordings: true, // Enable recordings
                        max_participants: 2, // Only AI and user
                        exp: Math.floor(Date.now() / 1000) + 1500, // Room expires in 25 minutes (25*60 = 1500 seconds)
                        eject_at_room_exp: true, // Eject participants when room expires
                        enable_recording: isTestInterview ? false : "cloud", // Don't record in test mode
                        start_video_off: false
                    },
                    body: {
                        // Custom properties for the agent
                        interviewName: interviewName || `Interview - ${userId} - ${new Date().toISOString()}`,
                        interviewContext: interviewContext,
                        closingStatement,
                        interviewerPersona,
                        toneAndCommunication,
                        interviewFlowPrinciples,
                        informationToShare,
                        aiName: aiName,
                        userName: userName,
                        customGreeting: customGreeting,
                        userId: userId,
                        userCV: userCV ? userCV : undefined,
                        fastapiURL: fastapiURL,
                        conversationId: newConversationId, // Use the generated UUID
                        s3BucketName: s3BucketName,
                        s3BucketRegion: s3BucketRegion,
                        awsAssumeRoleArn: awsAssumeRoleArn,
                        isTestInterview: isTestInterview, // Pass the test flag to the agent
                        concludingStatements: concludingStatements, // Pass the concluding statements
                        language: interviewLanguage, // Pass the language setting
                        voice: voiceKey ?? voice,
                        idleTimeoutMessage: idleTimeoutMessage || "It seems like you're busy right now. Have a nice day!", // Pass the idle timeout message
                        isDemo: language === "Demo",
                        companyContext: "",
                    }
                };

                const response = await axios.post(
                    requestUrl,
                    requestBody,
                    {
                        headers: {
                            'Authorization': `Bearer ${authToken}`,
                            'Content-Type': 'application/json'
                        }
                    }
                );

                console.log("API: Pipecat conversation created:", response.data);
                setCookie('interview_conversation_id', newConversationId, 1); // 1 day expiry

                // Update state with Pipecat's response format
                setConversationId(newConversationId); // Use token as ID if needed
                conversationIdRef.current = newConversationId; // Keep ref in sync — survives the late state-reset cleanup
                setConversationUrl(response.data.dailyRoom); // Pipecat returns dailyRoom as the URL
                setConversationToken(response.data.dailyToken); // Token for Daily join
                setStatus('active'); // Ready for Daily join
            } catch (error: unknown) {
                console.error('API: Failed to create Pipecat conversation:', error);
                conversationCreatedRef.current = false; // Allow retry if needed
                const axiosErr = error as { response?: { data?: { message?: string } } };
                catchErrorLog(axiosErr.response?.data?.message || 'Failed to create Pipecat conversation. Please check configuration or network.');
                setStatus('error');
            }
        }, [
            blockInterview, status, pipecatApiUrl, agentName,
            interviewName, interviewContext, customGreeting, fastapiURL,
            s3BucketName, s3BucketRegion, awsAssumeRoleArn, authToken, userId, isTestInterview, idleTimeoutMessage, conversationIdOverride
        ]);

        // Effect: Join/Leave Daily Call based on State
        useEffect(() => {
            if (!callObject) {
                console.log("Daily Effect: callObject not ready.");
                return; // Daily not initialized
            }

            const currentState = callObject.meetingState();
            console.log(`Daily Effect Check: Status='${status}', AI Joined='${AiJoined}', Daily State='${currentState}', URL='${conversationUrl ? 'Set' : 'Not Set'}'`);


            // Join Condition: Status is active, AI has joined (confirmed via WS), URL is available, and not already joined/joining
            if (status === 'active' && conversationUrl && conversationToken && (currentState === 'new' || currentState === 'left-meeting')) {
                console.log('Daily Effect: Conditions met, attempting to join call:', conversationUrl);
                callObject.join({
                    url: conversationUrl,
                    token: conversationToken, // Use the token from Pipecat
                    // Optionally set user name, tokens etc. here if needed
                    // userName: `User_${userId}`
                })
                    .then(() => {
                        console.log('Daily Effect: Join successful.');
                        setDailyJoined(true); // Update local state

                        console.log('muting user')
                        callObject.setLocalAudio(false) //mute local audio until user joins room

                        // callObject.startRecording({ type: "cloud" }); // Consider adding error handling for startRecording

                        // If showing the main UI, ensure devices are set in Daily
                        if (showVideoUI) {
                            console.log("Daily Effect: Setting devices after join...");
                            callObject.setInputDevicesAsync({
                                audioDeviceId: selectedAudioInputId || undefined,
                                videoDeviceId: selectedVideoId || undefined,
                            })
                                .catch(err => console.error("Daily Effect: Error setting input devices", err))

                            if (selectedAudioOutputId && 'setSinkId' in HTMLMediaElement.prototype) {
                                callObject.setOutputDeviceAsync({ outputDeviceId: selectedAudioOutputId })
                                    .catch(err => console.error("Daily Effect: Error setting output device", err));
                            }
                        }
                    })
                    .catch(err => {
                        console.error('Daily Effect: Failed to join call:', err);
                        setInternalErrorMessage(`Daily join error: ${err?.message || err}`);
                        // Decide if this constitutes a full error state
                        // setStatus('error'); // This might trigger leave/cleanup prematurely
                    });
            }
            // Leave Condition: Status indicates ending/completion/error, and currently joined/joining
            else if ((status === 'ending' || status === 'completed' || status === 'error') && (currentState === 'joined-meeting')) {
                console.log(`Daily Effect: Conditions met, leaving call (Status: ${status}, Daily State: ${currentState})`);
                callObject.leave()
                    .then(() => {
                        console.log('Daily Effect: Left meeting successfully.');
                        setDailyJoined(false); // Update local state
                    })
                    .catch(err => console.error('Daily Effect: Error leaving meeting:', err)); // Log error but usually proceed with state change
            }
            // Condition to handle showing main UI *before* joining Daily (e.g., confirming modal)
            else if (showVideoUI && !dailyJoined && (currentState === 'new' || currentState === 'left-meeting')) {
                // If we show the main UI but haven't joined Daily yet (e.g., after confirmation modal)
                // And if the AI has already joined, trigger the join
                if (status === 'active' && AiJoined && conversationUrl) {
                    console.log("Daily Effect: Main UI shown, AI ready, triggering join.");
                    // Join logic is handled by the first `if` condition, this block might be redundant
                    // Or could be used to trigger join explicitly *after* confirmation,
                    // but the main logic should cover it once `showVideoUI` is true *and* `AiJoined` is true.
                    // Ensure the state transitions allow the first `if` block to execute correctly.
                } else {
                    console.log("Daily Effect: Main UI shown, but not ready to join Daily yet.", { status, AiJoined, conversationUrl });
                }
            }


        }, [status, AiJoined, conversationUrl, callObject, showVideoUI, dailyJoined, selectedAudioInputId, selectedVideoId, selectedAudioOutputId, userId]); // Add relevant device IDs if needed for setting after join


        // Function to stop the local media stream used for preview/waiting room
        const stopLocalMediaStream = useCallback(() => {
            console.log("Stopping local media stream (if active)...");
            // Use ref to get the latest stream (avoids stale closure)
            const currentStream = mediaStreamRef.current;
            if (currentStream) {
                currentStream.getTracks().forEach((track) => {
                    console.log(`Stopping local track: ${track.kind} (${track.label})`);
                    track.stop();
                });
                mediaStreamRef.current = null;
                setMediaStream(null); // Clear the stream state
            }
            if (mediaStream) {
                mediaStream.getTracks().forEach((track) => {
                    console.log(`Stopping local track (from state): ${track.kind} (${track.label})`);
                    track.stop();
                });
                setMediaStream(null);
            }
            if (videoRef.current) {
                if (videoRef.current.srcObject instanceof MediaStream) {
                    const videoStream = videoRef.current.srcObject as MediaStream;
                    videoStream.getTracks().forEach((track) => {
                        console.log(`Stopping video element track: ${track.kind} (${track.label})`);
                        track.stop();
                    });
                }
                videoRef.current.srcObject = null; // Clear video element source
            }
            // NOTE: Do not reset cameraAccessGranted / micAccessGranted here.
            // Stopping the preview stream is unrelated to permission state — the
            // user has already granted access for the session, and clearing the
            // flags would re-trigger the "enable your camera & microphone" warning
            // every time we tear down the waiting-room stream (e.g. on
            // endConversation). Permission-revocation is handled at the
            // getUserMedia call sites where NotAllowedError is detected.
        }, [mediaStream]); // Dependency on mediaStream

        // End Conversation Logic
        const endConversation = useCallback(async (reason: string) => {
            console.log(`Action: endConversation triggered. Reason: ${reason}. Current status: ${status}. Test mode: ${isTestInterview}`);

            setStatus('ending'); // Set intermediate state immediately

            // --- Immediate Synchronous Media Cleanup (non-Daily streams) ---
            stopLocalMediaStream(); // Stop the preview/waiting room stream

            if (mediaStreamRef.current) {
                console.log("endConversation: Stopping remaining mediaStreamRef tracks");
                mediaStreamRef.current.getTracks().forEach(track => track.stop());
                mediaStreamRef.current = null;
            }
            if (videoRef.current?.srcObject instanceof MediaStream) {
                console.log("endConversation: Stopping remaining video element tracks");
                (videoRef.current.srcObject as MediaStream).getTracks().forEach(track => track.stop());
                videoRef.current.srcObject = null;
            }

            // Stop speech detection mic stream synchronously
            if (speech_status === 'listening' || speech_status === 'initializing') {
                console.log("Stopping speech detection during endConversation.");
                stopListening();
            }

            // Stop Daily persistent tracks immediately to kill browser device indicators
            // Safe for cloud recording — server has its own copy of media streams
            if (callObject) {
                try {
                    const localParticipant = callObject.participants()?.local;
                    if (localParticipant?.tracks) {
                        if (localParticipant.tracks.video?.persistentTrack) {
                            console.log("Stopping Daily video persistentTrack");
                            localParticipant.tracks.video.persistentTrack.stop();
                        }
                        if (localParticipant.tracks.audio?.persistentTrack) {
                            console.log("Stopping Daily audio persistentTrack");
                            localParticipant.tracks.audio.persistentTrack.stop();
                        }
                    }
                    callObject.setLocalVideo(false);
                    callObject.setLocalAudio(false);
                } catch (err) {
                    console.error("Error stopping Daily tracks:", err);
                }
            }

            // Stop Daily cloud recording (async — does not depend on local tracks)
            if (callObject && status == 'active') {
                try {
                    console.log("Daily: Stopping recording...");
                    if (!isTestInterview) {
                        await callObject.stopRecording();
                        // set a delayed request to call generateCandidateInsights and pass conversationId
                        setTimeout(() => {
                            console.log("Action: Generating candidate insights...");
                            if (isPracticeInterview) {
                                console.log("Practice interview - skipping insights generation");
                                generatePracticeInsights({
                                    "conversation_id": conversationId as string,
                                    "interview_segments": sections.map((section) => ({
                                        "duration": section.duration,
                                        "context": section.instruction,
                                    }))
                                })
                                    .then(() => {
                                        console.log("Action: Successfully Queued Practice Interview Insights.");
                                    })
                                    .catch((err) => {
                                        console.error("Action: Failed to generate practice insights:", err);
                                        catchErrorLog("Failed to generate practice insights. Please try again later.");
                                    });
                            }
                            else {
                                console.log("Generating insights for conversation:", conversationId);
                                generateCandidateInsights({
                                    "conversation_id": conversationId as string,
                                    "interview_segments": sections.map((section) => ({
                                        "duration": section.duration,
                                        "context": section.instruction,
                                    }))
                                })
                                    .then(() => {
                                        console.log("Action: Successfully Queued Interview Insights.");
                                    })
                                    .catch((err) => {
                                        console.error("Action: Failed to generate candidate insights:", err);
                                        catchErrorLog("Failed to generate candidate insights. Please try again later.");
                                    });
                            }

                        }, 30000) // delay 30 seconds to allow recording to finalize
                        console.log("Daily: Recording stopped.");
                    } else {
                        console.log("Test interview - no recording to stop");
                    }
                } catch (err) {
                    console.error("Daily: Error stopping recording:", err);
                }
            }

            // Leave and destroy Daily call (regardless of status to ensure media cleanup)
            if (callObject) {
                try {
                    const meetingState = callObject.meetingState();
                    if (meetingState === 'joined-meeting' || meetingState === 'joining-meeting') {
                        console.log("Daily: Leaving call...");
                        await callObject.leave();
                        console.log("Daily: Left call successfully.");
                    }
                    setDailyJoined(false);
                    // Destroy the Daily instance to fully release all media resources
                    console.log("Daily: Destroying instance to release media...");
                    await callObject.destroy();
                    console.log("Daily: Instance destroyed.");
                } catch (err) {
                    console.error("Daily: Error leaving/destroying call:", err);
                    // Try to destroy even if leave failed
                    try {
                        await callObject.destroy();
                    } catch (destroyErr) {
                        console.error("Daily: Error destroying after failed leave:", destroyErr);
                    }
                }
            }
            // --- End Explicit Media Cleanup ---

            // Only update backend status if not a test interview
            if (!isTestInterview) {
                if (reason === 'candidate_join_timeout') {
                    console.log("Timer expired, updating status to 'None'.");
                    updateInterviewStatusSafe({ new_status: "Pending Interview", conversation_id: (conversationIdRef.current ?? conversationId) as string })
                }
                else {
                    let userInterviewStatus: string = 'Complete';
                    // interview time duration < 25% of target duration
                    if (interviewTimeDuration < minDuration25Percent) {
                        userInterviewStatus = 'Aborted';
                    }
                    // interview time duration >= 25% and <= 50% of target duration
                    else if (interviewTimeDuration >= minDuration25Percent && interviewTimeDuration <= minDuration50Percent) {
                        userInterviewStatus = 'Partial';
                    }
                    console.log(`Setting status as ${userInterviewStatus} in ${interviewTimeDuration}.`);
                    updateInterviewStatusSafe({ new_status: userInterviewStatus, conversation_id: (conversationIdRef.current ?? conversationId) as string }).then(() => {
                        if (onInterviewStatusChange) {
                            onInterviewStatusChange(userInterviewStatus);
                        }
                        // submit the assessment with answers as conversationId
                        submitAssessmentV2({
                            assessment_id: interviewId,
                            answers: [{ id: "conversation_id", content: (conversationIdRef.current ?? conversationId) as string }],
                            job_id: job_id as string,
                            submission_type: "Interview"
                        }).catch((err) => {
                            catchErrorLog(`Failed to submit assessment. Err: ${err}`);
                        });
                        if (userInterviewStatus !== 'Aborted') {
                            console.log(`[EndConversation] Interview status: ${userInterviewStatus}, calling onCompletedInterview`);
                            onCompletedInterview();
                        } else {
                            console.log(`[EndConversation] Interview status: ${userInterviewStatus}, skipping onCompletedInterview`);
                        }
                    })
                }
            }

            setStatus('completed');
            console.log("Action: Status set to 'completed'.");

            // 4. Delayed state reset (allow time for cleanup effects)
            setTimeout(() => {
                console.log("Cleanup: Resetting non-essential state.");
                // Reset states that should clear for a potential next interview
                setConversationId(null);
                setConversationUrl(null);
                setAiJoined(false);
                setDailyJoined(false);
                setRequestSubmitted(false);
                setRequestType(null);
                setCountdownActive(false);
            }, 500);
        }, [ // Dependencies
            status, conversationId, authToken, pipecatApiUrl, callObject, stopLocalMediaStream, interviewStartTime, userId, interviewTimeDuration // Correct dependency
        ]);

        // Effect: Trigger Conversation Creation Once Ready
        useEffect(() => {
            // Create conversation only when:
            // - Not blocked by initial status check
            // - API key and relevant IDs are present
            // - Conversation hasn't been created yet
            // - Status is 'idle' (initial state)
            if (!blockInterview && authToken && agentName && !conversationCreatedRef.current && status === 'idle') {
                console.log("Effect: Triggering conversation creation.");
                createConversation();
            }
        }, [blockInterview, authToken, agentName, status, createConversation, interviewExpired]); // Dependencies

        // Imperative Handle for external control (e.g., parent component ending interview)
        useImperativeHandle(ref, () => ({
            endInterview: (reason: string = 'manual_external_request') => {
                console.log("Imperative Handle: endInterview called with reason:", reason);
                endConversation(reason);
            },
        }), [endConversation]); // Dependency on the memoized endConversation

        // --- Button Logic & UI Helpers ---
        const canStartInterview = cameraAccessGranted && micAccessGranted && AiJoined && dailyJoined; // Requires user media, AI joined, *and* user joined Daily

        const getButtonTooltip = (): string | undefined => {
            if (canStartInterview) return undefined; // No tooltip needed if enabled
            if (blockInterview) return "Session blocked. Please check status or request retake.";
            if (!cameraAccessGranted || !micAccessGranted) return "Please grant camera and microphone permissions.";
            if (!AiJoined) return (waitingForAiText ?? "Waiting for the AI to join the session.");
            if (!dailyJoined) return "Connecting to the video session..."; // Or "Click Join Session"?
            return "Please ensure media permissions are granted and AI has joined."; // Fallback
        };

        // Retry handler for the createInterview registration step. Hard
        // reloads the page so every effect re-runs from scratch — simpler
        // and avoids leaving partial state from the failed attempt.
        const handleRetryCreateInterview = () => {
            if (isRetryingCreateInterview) return;
            setIsRetryingCreateInterview(true);
            window.location.reload();
        };

        // --- Event Handlers ---
        const handleStartInterviewClick = async () => {
            if (!cameraAccessGranted || !micAccessGranted) {
                console.warn("Start clicked but media permissions not granted.");
                setInternalErrorMessage("Please grant camera and microphone access first.");
                return;
            }
            if (blockInterview) {
                console.warn("Start clicked but interview is blocked.");
                // Error message should already be set by checkInterviewStatus
                return;
            }
            if (!AiJoined) {
                console.warn("Start clicked but AI has not joined yet.");
                setInternalErrorMessage((waitingForAiText ?? "Please wait for the AI to join."));
                return;
            }
            // Audio level gate (ticket #1870):
            // - "denied"/"error" => hard gate (no mic available — permission
            //   blocked or `getUserMedia` threw, e.g. NotFoundError)
            // - "weak"           => soft gate (TEMPORARILY DISABLED — see below)
            // - "low"            => DO NOT hard-gate. On Android, AGC/built-in
            //   mics often stay under the 15% threshold even when working
            //   correctly, which previously triggered a false-positive
            //   "No microphone detected" popup. The audio bar already turns
            //   red as a visual cue; let the candidate proceed.
            // - "strong"/"idle"  => proceed normally
            if (
                waitingRoomAudioStatus === "denied" ||
                waitingRoomAudioStatus === "error"
            ) {
                setAudioGateVariant("hard");
                return;
            }
            // Soft gate temporarily disabled while we collect Mixpanel telemetry
            // to validate the WEAK_PERCENT threshold against real candidate
            // hardware. The hysteresis tuning is still suspect for some mic
            // classes (especially cheap laptop built-ins under AGC), so showing
            // the soft gate now risks too many false positives. Re-enable once
            // we've confirmed the threshold is correct per mic_label cohort.
            // if (waitingRoomAudioStatus === "weak") {
            //     setAudioGateVariant("soft");
            //     return;
            // }
            // Showconfirmation modal
            if (isPracticeInterview) {
                await handleConfirmInterview();
            } else {
                setShowConfirmationModal(true);
            }
        };

        // Proceeds past the soft gate ("Continue anyway"). Bypasses the audio
        // check but still routes through the normal confirmation flow so that
        // recording, practice mode, etc. behave the same.
        const proceedPastAudioGate = async () => {
            if (isPracticeInterview) {
                await handleConfirmInterview();
            } else {
                setShowConfirmationModal(true);
            }
        };

        // "Check my microphone" CTA from the gate modal: open the device picker
        // (dropdown on desktop, dialog on mobile). Tailwind's sm breakpoint is 640px.
        const openMicrophonePicker = () => {
            const isMobile = typeof window !== "undefined"
                && window.matchMedia("(max-width: 639px)").matches;
            if (isMobile) {
                setIsOpenDialogMic(true);
            } else {
                setIsMicDropdownOpen(true);
            }
        };

        const onHandleEndConfirmInterview = useCallback((isDontShowAgain: boolean) => {
            // Dialog only shows at 51-99% completion, so always past 50% threshold
            endConversation('user_request');

            if (isDontShowAgain) {
                setCookie('is-skip-confirm-end-interview', "true", 7) // expired 7 days
            }
            setIsDialogConfirmEndInterview(false);
        }, [endConversation]);

        const handleConfirmInterview = async () => {
            setLoadingJoin(true);

            if (!isTestInterview && job_id) {
                recordAIInterviewStarted(job_id).catch((error) => {
                    catchErrorLog(`Error recording AI interview started: ${error}`);
                });
            }

            if (callObject) {
                // stopListening(); // Stop local speech detection if active
                if (!isTestInterview) {
                    callObject?.startRecording({
                        type: "cloud",
                        layout: {
                            preset: "custom",
                            composition_params: {
                                "mode": "single",
                                "showTextOverlay": false,
                                "showImageOverlay": true,
                                "showBannerOverlay": false,
                                "showWebFrameOverlay": false,
                                "showSidebar": false,
                                "showTitleSlate": false,
                                "enableAutoOpeningSlate": false,
                                "videoSettings.maxCamStreams": 1,
                                "videoSettings.preferScreenshare": false,
                                "videoSettings.omitPausedVideo": false,
                                "videoSettings.omitAudioOnly": false,
                                "videoSettings.omitExtraScreenshares": false,
                                "videoSettings.showParticipantLabels": false,
                                "videoSettings.roundedCorners": false,
                                "videoSettings.cornerRadius_gu": 1.2,
                                "videoSettings.scaleMode": "fit",
                                "videoSettings.scaleModeForScreenshare": "fit",
                                "videoSettings.placeholder.bgColor": "rgb(0, 50, 80)",
                                "videoSettings.highlight.color": "rgb(255, 255, 255)",
                                "videoSettings.highlight.stroke_gu": 0.2,
                                "videoSettings.split.margin_gu": 0,
                                "videoSettings.grid.useDominantForSharing": false,
                                "videoSettings.grid.highlightDominant": true,
                                "videoSettings.grid.preserveAspectRatio": false,
                                "image.assetName": "watermark",
                                "image.position": "top-right",
                                "image.zPosition": "foreground",
                                "image.fullScreen": false,
                                "image.fullScreenScaleMode": "fit",
                                "image.aspectRatio": 1.678,
                                "image.height_gu": 3,
                                "image.margin_gu": 1.7,
                                "image.opacity": 0.9,
                                "image.enableFade": true,
                            },
                            session_assets: {
                                'images/watermark': import.meta.env.VITE_WATERMARK_IMG_URL
                            }
                        }
                    });
                    console.log("Congrats: Starting recording before user joins...");

                    // setLoadingJoin(false);
                } else {
                    // Test interview - not starting recording
                    console.log("Test interview - not starting recording");

                    // Need to send app message with type "conversation.start_test_interview_request"
                    const interaction = {
                        message_type: "conversation",
                        event_type: "conversation.start_test_interview_request", // Custom event for test interviews
                        conversation_id: conversationId
                    };
                    callObject.sendAppMessage(interaction, '*'); // Send to all participants
                    console.log("Congrats: Starting test interview without recording");
                }
                // callObject.updateInputSettings({ audio: { processor: { type: "noise-cancellation" } } });
                // console.log("Congrats2: Started noise cancellation");
                // setLoadingJoin(false);
            }
            setIsRecordingStarting(true);
            // Rest of logic is handles by the on-recording Daily Event
        };


        // Countdown and Retake/Issue Logic
        useEffect(() => {
            // Start countdown timer only when AI joins, user has media, and not already in main UI
            if (AiJoined && canStartInterview && !countdownActive && !showVideoUI && !interviewExpired && !blockInterview) {
                console.log("Starting join countdown timer.");
                setCountdownActive(true);

                //   updateInterviewStatusV2({ new_status: "Interviewer Ready" })
                //     .then((res) => console.log("Interview status updated on backend:", res))
                //     .catch((err) => console.error("Error updating backend interview status:", err));
            }
        }, [AiJoined, canStartInterview, countdownActive, showVideoUI, interviewExpired, blockInterview]); // Dependencies

        const handleTimeout = useCallback(() => {
            console.log("Interview join timer expired.");
            if (!showVideoUI) { // Only expire if user hasn't joined the main UI yet
                if (!isTestInterview) {
                    updateInterviewStatusSafe({ new_status: "Pending Interview", conversation_id: (conversationIdRef.current ?? conversationId) as string })
                }
                setInterviewExpired(true);
                setCountdownActive(false);
                setInternalErrorMessage("You took too long to join. Please request to retake.");
                setStatus('error');
                // Optionally end the conversation immediately if timeout occurs
                endConversation('candidate_join_timeout');
            }
        }, [showVideoUI, isTestInterview, conversationId, endConversation]);

        const handleRetakeRequest = (reason: string) => {
            console.log("Retake request submitted with reason:", reason);
            // TODO: Send request to backend API
            // e.g., api.submitRetakeRequest({ userId, reason, conversationId });
            setRequestSubmitted(true);
            setRequestType("retake");
            setIsRetakeDialogOpen(false);
            // Consider ending the conversation if a retake is requested and session is active
            if (conversationId && status !== 'completed' && status !== 'ending') {
                endConversation('retake_requested');
            }
        };

        const handleIssueSubmit = (issueType: string, details: string) => {
            console.log("Issue reported:", { userId, issueType, details, conversationId });
            setRequestType("issue");
            setIsIssuesDialogOpen(false);
            // Consider ending the conversation if an issue is reported and session is active
            if (conversationId && status !== 'completed' && status !== 'ending') {
                endConversation(`issue_reported:_${issueType}`);
            }

            snackbar.showSuccess("Issue reported to the team. Please try again.");

            if (!onReload) {
                setTimeout(() => {
                    window.location.reload(); // For now, just reload to reset state
                }, 2000)
            } else {
                onReload();
            }
        };

        // Helper to make AI speak (if needed)

        const endAiCall = () => {
            if (callObject && conversationId && callObject.meetingState() === 'joined-meeting') {
                console.log("Ending a call:");
                const interaction = {
                    message_type: "conversation",
                    event_type: "conversation.end",
                    conversation_id: conversationId,
                    properties: {},
                };
                callObject.sendAppMessage(interaction, '*') // Send to all (AI should pick it up)
                console.log("AI 'end' message sent:", interaction);

                if (!isTestInterview) {
                    const userInterviewStatus = 'Complete';

                    updateInterviewStatusSafe({ new_status: userInterviewStatus, conversation_id: (conversationIdRef.current ?? conversationId) as string }).then(() => {
                        if (onInterviewStatusChange) {
                            onInterviewStatusChange(userInterviewStatus);
                        }
                        // submit the assessment with answers as conversationId
                        submitAssessmentV2({
                            assessment_id: interviewId,
                            answers: [{ id: "conversation_id", content: (conversationIdRef.current ?? conversationId) as string }],
                            job_id: job_id as string,
                            submission_type: "Interview"
                        }).catch((err) => {
                            catchErrorLog(`Failed to submit assessment. Err: ${err}`);
                        });
                        console.log(`[AIEnd] Interview status: Complete, calling onCompletedInterview`);
                        onCompletedInterview();

                        // Audio detection telemetry (#1870/#1871). interview_id +
                        // user_id are included so we can correlate Mixpanel
                        // distributions back to specific sessions in our DB
                        // when investigating threshold edge cases.
                        const audioStats = getAudioStats();
                        trackEvent('ai_interview_completed', {
                            job_id: jobid || "",
                            device_type: `Audio: ${audioInputName} | Video: ${videoName}`,
                            interview_id: interviewId,
                            user_id: userId,
                            mic_label: audioStats.micLabel,
                            audio_speaking_histogram: audioStats.speakingHistogram,
                            audio_silent_histogram: audioStats.silentHistogram,
                            audio_speaking_samples: audioStats.speakingSamples,
                            audio_silent_samples: audioStats.silentSamples,
                            audio_max_percent: audioStats.maxPercent,
                            audio_transitions_low: audioStats.transitionsLow,
                            audio_transitions_weak: audioStats.transitionsWeak,
                            audio_transitions_noise: audioStats.transitionsNoise,
                            audio_transitions_strong: audioStats.transitionsStrong,
                            audio_ever_saw_strong: audioStats.everSawStrong,
                            audio_session_duration_ms: audioStats.sessionDurationMs,
                        })
                    });
                }
            } else {
                // Daily already left / no conversation id when the last-step timer
                // fired (local network drop, or the AI ended and Daily disconnected
                // first). This used to only console.warn and SKIP the entire
                // completion chain — v2/update-status never called, parent never
                // notified, candidate stranded on a dead screen. Fall back to
                // endConversation, which owns the same duration-based status update
                // + onInterviewStatusChange + submitAssessmentV2 path.
                catchErrorLog(
                    `[interview] endAiCall guard failed (meetingState=${callObject?.meetingState() ?? "no-callObject"}, conversationId=${conversationId ? "set" : "null"}) — falling back to endConversation`
                );
                endConversation('ai_end_fallback');
            }
        };

        const updateConversationContext = (context: string) => {
            if (callObject && conversationId) {
                console.log("Updating conversation context:", context);
                const interaction = {
                    message_type: "conversation",
                    event_type: "conversation.overwrite_llm_context", // Using echo for direct TTS
                    conversation_id: conversationId,
                    properties: {
                        context: context
                    },
                };
                callObject.sendAppMessage(interaction, '*') // Send to all (AI should pick it up)
                console.log("Conversation context updated:", interaction);
            } else {
                console.warn("Failed to update context", { joined: callObject?.meetingState(), conversationId });
            }
        };

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const sendTestEndMessage = () => {
            if (callObject && conversationId && callObject.meetingState() === 'joined-meeting') {
                console.log("Sending test end message:");
                const interaction = {
                    message_type: "conversation",
                    event_type: "conversation.test_end",
                    conversation_id: conversationId,
                    properties: {},
                };
                callObject.sendAppMessage(interaction, '*') // Send to all (AI should pick it up)
                console.log("Test end message sent:", interaction);
            } else {
                console.warn("Cannot send test end message: Daily not joined or no conversation ID.", { joined: callObject?.meetingState(), conversationId });
            }
        };

        // --- Observe Local User Audio Level ---
        const speakingTimerRef = useRef<NodeJS.Timeout | null>(null);

        // Cleanup timer on unmount
        useEffect(() => {
            return () => {
                if (speakingTimerRef.current) {
                    clearTimeout(speakingTimerRef.current);
                }
            };
        }, []);

        // --- Get Selected Device Names for UI ---
        const getSelectedDeviceNames = () => {
            const audioInput = audioInputDevices.find(d => d.deviceId === selectedAudioInputId);
            const audioOutput = audioOutputDevices.find(d => d.deviceId === selectedAudioOutputId);
            const video = videoDevices.find(d => d.deviceId === selectedVideoId);

            return {
                audioInputName: audioInput?.label || (selectedAudioInputId ? 'Selected Mic' : 'Default Mic'),
                audioOutputName: audioOutput?.label || (selectedAudioOutputId ? 'Selected Speaker' : 'Default Speaker'),
                videoName: video?.label || (selectedVideoId ? 'Selected Camera' : 'Default Camera')
            };
        };

        const { audioInputName, audioOutputName, videoName } = getSelectedDeviceNames();

        // --- Daily Event Handling ---
        const handleAppMessage: DailyAppMessageCallback = useCallback(
            (event: DailyEventObjectAppMessage) => {
                const pipecat_event = event.data as PipecatAppMessageData

                console.log('pipecat_event', pipecat_event)

                // check if bot has decided to end the call
                if (pipecat_event.type === "conversation.end") {
                    console.log("VideoContainer: Pipecat Interaction Received: conversation.end");
                    setIsConcluding(true);
                    setIsConversationEnded(true);
                }

                if (pipecat_event.type === "bot-tts-started") {
                    // Bot is streaming text
                    setIsAiSpeaking(true);
                }
                else if (pipecat_event.type === "bot-stopped-speaking") {
                    // Bot has finished speaking
                    setTimeout(() => {
                        setIsAiSpeaking(false)
                    }, 2000)
                }
                else if (pipecat_event.type === "conversation.user_can_join") {
                    // Bot signals it's ready for the user to join — release the
                    // Start Interview button gate set when the "Time to join"
                    // row appeared.
                    console.log("Received conversation.user_can_join, releasing test countdown gate");
                }
                else if (pipecat_event.type === "conversation.test_interview_start") {
                    console.log("Received Test start message:", pipecat_event);
                    setShowConfirmationModal(false);

                    if (callObject) {
                        callObject.setLocalAudio(true);

                        // Mark interview start time
                        setInterviewStartTime(Date.now());

                        setShowVideoUI(true); // Show the main VideoContainer UI
                        setIsRecordingStarting(false);

                        // Set initial context (Step 0)
                        console.log("Setting initial interview context (Step 0)");
                        updateConversationContext(interviewContexts[0]);
                        setInterviewStep(0); // Explicitly set step to 0
                        // wait 1 second
                        // setTimeout(() => {
                        //     makeAiSay(`${customGreeting ? customGreeting : "Hey, thanks for joining the call today. Are you ready for your ourteam interview?"}`)
                        // }, 1000)
                    }
                }
                const tavusMessage = event.data as TavusAppMessageData;
                if (!tavusMessage || !tavusMessage.event_type) return;

                console.log(
                    `VideoContainer: Tavus Interaction Received: ${tavusMessage.event_type}`,
                    tavusMessage.properties
                );

                switch (tavusMessage.event_type) {
                    case "conversation.utterance":
                        if (tavusMessage.properties?.role === 'user') {
                            console.log("User spoke, resetting pause state");
                        }
                        // console.log("Updating last utterance time to:", Date.now());
                        break;
                    case "conversation.replica.started_speaking":
                        console.log("AI started speaking:", tavusMessage.properties);
                        // setLastUtteranceTime(Date.now());
                        // setIsAiSpeaking(true);
                        break;
                    case "conversation.replica.stopped_speaking":
                        // console.log("Updating last utterance time to:", Date.now());
                        // setLastUtteranceTime(Date.now());
                        // setIsAiSpeaking(false);
                        break;
                    default:
                        console.warn(
                            "VideoContainer: Unhandled Tavus Interaction type:",
                            tavusMessage.event_type
                        );
                }
            },
            [callObject, interviewContexts, customGreeting, isRecordingStarting, conversationId, userName]
        );

        useDailyEvent("app-message", handleAppMessage);

        useDailyEvent("participant-joined", useCallback((event) => {
            console.log("Participant Joined:", event.participant);
            setAiJoined(true);
        }, []));

        useDailyEvent("participant-left", useCallback((event) => {
            console.log("Participant left:", event.participant);
            setIsConcluding(true)
        }, []));

        useDailyEvent("recording-started", useCallback((event) => {
            console.log("Recording started:", event);
            setShowConfirmationModal(false);

            // Create the interview record in backend *before* showing the main UI
            if (conversationId && callObject) {
                console.log("Creating interview record for conversation:", conversationId);
                try {
                    // Start recording now that user has joined
                    console.log("Daily Effect: Starting recording after join...");
                    callObject.setLocalAudio(true);

                    // Mark interview start time
                    setInterviewStartTime(Date.now());

                    // Only proceed if creation was successful
                    setShowVideoUI(true); // Show the main VideoContainer UI
                    setIsRecordingStarting(false);

                    // Set initial context (Step 0)
                    console.log("Setting initial interview context (Step 0)");
                    updateConversationContext(interviewContexts[0]);
                    setInterviewStep(0); // Explicitly set step to 0
                    // wait 1 second
                    // setTimeout(() => {
                    //     makeAiSay(`${customGreeting ? customGreeting : "Hey, thanks for joining the call today. Are you ready for your ourteam interview?"}`)
                    // }, 1000)

                } catch (error: unknown) {
                    console.error("Failed to create interview record:", error);
                    setInternalErrorMessage(`Error starting interview: ${error instanceof Error ? error.message : "Unknown error"}. Please try again.`);
                }

            } else {
                console.error("Cannot confirm interview: conversationId is missing.");
                setInternalErrorMessage("Cannot start interview: Session ID missing.");
            }
        }, [conversationId, callObject, interviewContexts, userName, isRecordingStarting, customGreeting]));        // Effect to progress interview steps automatically

        useEffect(() => {
            // Create dynamic step durations array from sections prop
            // Index 0 is initial delay (0), then each section's duration
            const stepDurations = [0, ...sections.map(section => section.duration)];
            console.log("Dynamic step durations created from sections:", stepDurations);

            let timeoutId: NodeJS.Timeout | null = null;

            // Only run the timer when the main video UI is shown and the call is active
            if (showVideoUI && callObject && conversationId && callObject.meetingState() === 'joined-meeting') {
                const currentStep = interviewStep;
                const nextStep = currentStep + 1;

                console.log(`Interview step effect running. Current step: ${currentStep} interview Context count: ${interviewContexts.length} value: ${nextStep < interviewContexts.length}`);

                // Check if there is a next step context
                if (nextStep < interviewContexts.length) {
                    // Get the duration for the *current* step (time until we move to the *next* step)
                    // Use stepDurations[nextStep] because stepDurations[1] is duration for step 0, etc.
                    const durationInSeconds = stepDurations[nextStep];
                    console.log("section interview durations:", durationInSeconds);
                    if (durationInSeconds > 0) {
                        console.log(`Scheduling transition from step ${currentStep} to step ${nextStep} in ${durationInSeconds} seconds.`);

                        timeoutId = setTimeout(() => {
                            console.log(`Timer fired: Advancing from step ${currentStep} to step ${nextStep}.`);
                            // Update context to the next step's context
                            updateConversationContext(interviewContexts[nextStep]);
                            // Update the state to reflect the new current step
                            setInterviewStep(nextStep);
                        }, durationInSeconds * 1000);

                    } else {
                        console.log(`Duration for step ${currentStep} is 0 or invalid (${durationInSeconds}s). Not scheduling automatic advance to step ${nextStep}.`);
                        // If duration is 0, you might want to advance immediately or handle differently
                        // For now, it just won't schedule the next step automatically via timer.
                    }
                } else {
                    // We have completed the last step (index interviewContexts.length - 1)
                    // Check the duration associated with the *last* step (stepDurations[interviewContexts.length])
                    const lastStepDuration = stepDurations[interviewContexts.length]; // e.g., stepDurations[6] for 6 contexts (0-5)
                    if (lastStepDuration > 0) {
                        console.log(`Last step (${currentStep}) reached. Scheduling end of interview in ${lastStepDuration} seconds based on its duration.`);
                        timeoutId = setTimeout(() => {
                            console.log("Timer fired after last step's duration. Ending conversation.");
                            // Assuming the last context handles the closing remarks,
                            // we might just end the conversation after its duration.
                            endAiCall()
                        }, lastStepDuration * 1000);
                    } else {
                        console.log(`Last step (${currentStep}) reached. No specific duration to wait before ending, or duration is 0.`);
                        // Consider if you want to end immediately or rely on the last context's flow.
                        // Perhaps the last context itself should trigger the end via an app message?
                    }
                }
            }

            // Cleanup function: Clear the timeout when the effect re-runs or component unmounts
            return () => {
                if (timeoutId) {
                    console.log("Clearing interview step timer.");
                    clearTimeout(timeoutId);
                }
            };            // Dependencies: Re-run when UI shows/hides, call state changes, or the interviewStep state updates.
        }, [showVideoUI, callObject, conversationId, interviewStep, sections]);

        // Ref to hold stopListening function for cleanup without causing re-runs
        const stopListeningRef = useRef(stopListening);
        useEffect(() => {
            stopListeningRef.current = stopListening;
        }, [stopListening]);

        // Mirror status into a ref so beforeunload can read the latest value
        // without re-subscribing the listener on every status change.
        const statusRef = useRef(status);
        useEffect(() => {
            statusRef.current = status;
        }, [status]);

        // Add this new useEffect for comprehensive media cleanup on unmount
        useEffect(() => {
            // Store video ref at mount time for cleanup (to satisfy React lint rules)
            const currentVideoRefAtMount = videoRef.current;

            // Handle browser tab/window close — stop all media tracks synchronously.
            // Also prompt the browser's native "Leave site?" dialog while an interview
            // is actively running, so candidates don't lose the session by accident.
            const stopAllMedia = () => {
                console.log("pagehide/beforeunload: Stopping all media tracks");
                const stream = mediaStreamRef.current;
                if (stream) {
                    stream.getTracks().forEach(track => track.stop());
                }
                const co = callObjectRef.current;
                if (co) {
                    try {
                        const local = co.participants()?.local;
                        if (local?.tracks?.video?.persistentTrack) local.tracks.video.persistentTrack.stop();
                        if (local?.tracks?.audio?.persistentTrack) local.tracks.audio.persistentTrack.stop();
                    } catch { /* ignore */ }
                    try { co.destroy(); } catch { /* ignore */ }
                }
            };

            // pagehide fires only when the page is actually being torn down,
            // so it's safe to release media here without racing the confirm dialog.
            const handlePageHide = () => {
                stopAllMedia();
            };
            window.addEventListener("pagehide", handlePageHide);

            // Use a single window.onbeforeunload handler — the return-value
            // pattern is the most universally supported mechanism across
            // Chrome, Firefox, Safari, and Arc.  Using both addEventListener
            // and onbeforeunload can cause conflicts in Firefox.
            const previousOnBeforeUnload = window.onbeforeunload;
            window.onbeforeunload = function (e: BeforeUnloadEvent) {
                if (statusRef.current === "active") {
                    e.preventDefault();
                    // Non-empty string — Windows Chrome skips the dialog
                    // when returnValue is falsy. Text is browser-controlled.
                    e.returnValue = leavePromptText ?? "Leave interview?";
                    // Don't stop media here — if the user cancels the native
                    // dialog they stay in the interview and need mic/camera
                    // still live. `pagehide` runs if they confirm leaving.
                    return leavePromptText ?? "Leave interview?";
                }
                stopAllMedia();
                if (previousOnBeforeUnload) {
                    return previousOnBeforeUnload.call(window, e);
                }
                return undefined;
            };

            // This effect is only for cleanup when component unmounts
            return () => {
                window.removeEventListener("pagehide", handlePageHide);
                window.onbeforeunload = previousOnBeforeUnload;
                console.log("Component unmounting: Cleaning up ALL media resources");

                // Mark as unmounted to prevent async operations from creating orphaned streams
                isMountedRef.current = false;

                // Use refs to get the latest values (not stale closure values)
                const currentMediaStream = mediaStreamRef.current;
                const currentCallObject = callObjectRef.current; // Use ref instead of stale closure
                // Use the ref captured at mount for video element (satisfies lint rule)
                const currentVideoRef = currentVideoRefAtMount || videoRef.current;

                // 1. Stop all tracks from Daily call first (if active)
                if (currentCallObject) {
                    try {
                        const localParticipant = currentCallObject.participants()?.local;
                        if (localParticipant?.tracks) {
                            // Stop video track
                            if (localParticipant.tracks.video?.persistentTrack) {
                                console.log("Stopping Daily video track");
                                localParticipant.tracks.video.persistentTrack.stop();
                            }
                            // Stop audio track
                            if (localParticipant.tracks.audio?.persistentTrack) {
                                console.log("Stopping Daily audio track");
                                localParticipant.tracks.audio.persistentTrack.stop();
                            }
                        }

                        // Stop camera and microphone via Daily API
                        if (currentCallObject.meetingState() === 'joined-meeting') {
                            console.log("Stopping camera and mic via Daily API");
                            currentCallObject.setLocalVideo(false);
                            currentCallObject.setLocalAudio(false);
                        }
                    } catch (err) {
                        console.error("Error stopping Daily tracks:", err);
                    }
                }

                // 2. Stop all tracks in the mediaStream state
                if (currentMediaStream) {
                    console.log("Stopping mediaStream tracks");
                    currentMediaStream.getTracks().forEach(track => {
                        console.log(`Stopping ${track.kind} track: ${track.label}`);
                        track.stop();
                    });
                }

                // 3. Also check for any streams directly attached to video element
                if (currentVideoRef?.srcObject instanceof MediaStream) {
                    console.log("Stopping video element stream tracks");
                    const videoElementStream = currentVideoRef.srcObject as MediaStream;
                    videoElementStream.getTracks().forEach(track => {
                        console.log(`Stopping video element ${track.kind} track: ${track.label}`);
                        track.stop();
                    });
                    currentVideoRef.srcObject = null;
                }

                // 4. Clear Daily call if still active
                // Use destroy() directly — it handles leaving internally and is more reliable
                // than chaining leave().then(destroy()) in unmount cleanup where promises may not resolve
                if (currentCallObject) {
                    try {
                        const meetingState = currentCallObject.meetingState();
                        console.log(`Destroying Daily instance (meetingState: ${meetingState})`);
                        currentCallObject.destroy().catch(err => console.error("Error destroying Daily instance:", err));
                    } catch (err) {
                        console.error("Error destroying Daily instance:", err);
                    }
                }

                // 5. Clear mediaStreamRef to prevent any further usage
                mediaStreamRef.current = null;
                callObjectRef.current = null;

                // 6. Stop local speech detection using ref to get latest function
                console.log("Stopping speech detection on unmount");
                if (stopListeningRef.current) {
                    stopListeningRef.current();
                }

                console.log("Component unmount cleanup completed");
            };
        }, []); // Empty dependency - only run on mount/unmount

        const handleDialogConfirmEndInterview = useCallback(() => {
            const isDontShowAgainConfirmEndInterview = getCookie("is-skip-confirm-end-interview") || false;

            const completionPercent = maxDurationInterview > 0
                ? (interviewTimeDuration / maxDurationInterview) * 100
                : 100;

            if (isDontShowAgainConfirmEndInterview) {
                if (interviewTimeDuration <= minDuration50Percent) {
                    endConversation('interview_too_short');
                } else {
                    endConversation('user_request')
                }
                return;
            }

            if (completionPercent < 100) {
                setIsDialogConfirmEndInterview(true);
            } else {
                endConversation('user_request');
                if (handleEndSession) {
                    handleEndSession();
                }
            }
        }, [interviewTimeDuration, minDuration50Percent, maxDurationInterview, endConversation])

        // --- Render Logic ---
        return (
            <>
                <DailyAudio
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    ref={audioRef as any} // Type assertion to avoid TS error
                    onPlayFailed={handleAudioPlayFailed}
                    maxSpeakers={3} // Adjust as needed
                />

                {showVideoUI ? (
                    <>
                        <VideoContainer
                            status={status}
                            onEndInterview={handleDialogConfirmEndInterview}
                            interviewTitle={interviewName}
                            tavusCallbacks={tavusCallbacks as TavusInteractionCallbacks} // Pass interaction callbacks
                            onCloseDialogSuccessRecord={onCloseModal} // Callback when interview success dialog is closed
                            onIssueReport={() => {
                                // VideoContainer fires this from inside the live session.
                                setReportTypeIn("live-session");
                                setIsIssuesDialogOpen(true);
                            }}
                            isInterviewConcluding={isConcluding} // Pass the concluding state
                            isConversationEnded={isConversationEnded} // Pass conversation ended state
                            onConcluding={setIsConcluding} // Pass callback to update concluding state
                            concludingCountdown={countdownSeconds} // Pass the countdown timer value
                            customGreeting={customGreeting} // Pass custom greeting
                            interviewStartTime={interviewStartTime} // Use the stored start time, not current time
                            userId={userId}
                            isTestInterview={isTestInterview} // Pass test mode flag
                            conversationId={conversationId as string}
                            assessmentId={interviewId}
                            currentCameraId={selectedVideoId}
                            currentMicrophoneId={selectedAudioInputId}
                            aiName={aiName} // Pass the AI interviewer name
                            aiLogoSrc={aiLogoSrc} // Pass the AI logo source
                            interviewLanguage={interviewLanguage}
                            audioStatus={audioStatus}
                            connectionStatus={connectionQuality}
                            hasFormAssessment={hasFormAssessment}
                            hasEligibilityCheck={hasEligibilityCheck}
                            tipText={tipText}
                        />
                    </>
                ) : (
                    // waiting room UI
                    <div className="w-full max-w-6xl" id="waiting-room">
                            {/* {showMobileWarning && (
                            <div className="bg-yellow-50 sm:hidden flex text-sm dark:bg-yellow-900/20 border border-yellow-200 rounded-lg px-2 py-1 text-yellow-800 dark:text-yellow-300 items-center mb-2 gap-2">
                                <span className="flex-1">Looks like you’re on your phone—switch to a laptop or desktop for a smoother experience.</span>
                                <button
                                    onClick={() => setShowMobileWarning(false)}
                                    className="flex-shrink-0 p-0.5 rounded hover:bg-yellow-200 dark:hover:bg-yellow-800 transition-colors"
                                >
                                    <XIcon className="h-4 w-4" />
                                </button>
                            </div>
                        )} */}
                            {/* Mobile-only browser-experience hint. The component
                            self-gates on viewport (≤ 639px) and returns null
                            on desktop, so we always render it here regardless
                            of `isBrowserSupported`. */}
                            <BrowserCompatibilityWarning />
                            {/* Mobile-only collapsible guide for enabling
                            camera + microphone permissions. Same self-gating
                            (`sm:hidden`) — invisible on desktop. */}
                            <MicCameraPermissionGuide />
                        <div className="flex flex-row justify-between items-start">
                            <div className="flex flex-col">
                                <h1 className="text-xl sm:text-3xl font-semibold sm:font-bold text-gray-900 dark:text-white mb-2">
                                    {interviewName}
                                </h1>
                                <div className="grid md:grid-cols-[1fr,265px] gap-1 sm:gap-4">
                                    <div className="text-gray-600 dark:text-gray-400 mb-6 flex flex-row items-center gap-2">
                                        <div className="flex-shrink-0">
                                            <span className="text-sm font-medium text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-800 px-3 py-1 rounded-full">
                                                {formatDuration(maxDurationInterview, "min")}
                                            </span>
                                        </div>
                                        <div>
                                            <p className="md:ml-2 text-sm">
                                                {description}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="grid md:grid-cols-[1.2fr,1fr] gap-8">
                            {/* Left side - Video preview & Controls */}
                            <div className="flex flex-col gap-2">
                                {/* Video Preview Area */}
                                <div className="rounded-xl border text-card-foreground shadow overflow-hidden h-[400px] bg-gray-100 dark:bg-gray-800 relative ">
                                    <video
                                        ref={videoRef}
                                        autoPlay
                                        muted
                                        playsInline
                                        className="w-full h-full object-cover"
                                    />

                                    {/* "You" label with audio visualizer */}
                                    <div className="absolute bottom-3 left-3">
                                        {status !== 'connecting' && mediaStream && (
                                            <div className="text-sm font-medium text-white bg-black/60 px-3 py-1.5 rounded-lg flex flex-row items-center gap-2">
                                                You
                                                {speech_status === 'listening' && audioStatus === 'weak' ? (
                                                    <Tooltip
                                                        content={(
                                                            <p className="text-xs">Weak audio detected, try adjusting your microphone settings.</p>
                                                        )}
                                                    >
                                                        <button
                                                            onClick={() => setIsMicDropdownOpen(true)}
                                                            className="sm:flex items-center hover:opacity-80 transition-opacity hidden"
                                                        >
                                                            <div className="relative h-4 w-4 bg-yellow-500 rounded-full flex items-center justify-center">
                                                                <span className="text-[10px] font-bold text-gray-900 leading-none">!</span>
                                                            </div>
                                                        </button>
                                                        <button
                                                            onClick={() => setIsOpenDialogMic(true)}
                                                            className="flex items-center hover:opacity-80 transition-opacity sm:hidden"
                                                        >
                                                            <div className="relative h-4 w-4 bg-yellow-500 rounded-full flex items-center justify-center">
                                                                <span className="text-[10px] font-bold text-gray-900 leading-none">!</span>
                                                            </div>
                                                        </button>
                                                    </Tooltip>
                                                ) : (isTalking) && (
                                                    <EnhancedAudioVisualizer
                                                        isActive={isTalking}
                                                        variant="wave"
                                                        barCount={4}
                                                        color="green"
                                                    />
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <div className="absolute bottom-3 right-3 sm:hidden block">
                                        <div className="flex flex-row sm:hidden items-center opacity-80 gap-1 bg-black/70 text-white  px-2 py-1.5 rounded-full shadow-sm w-fit">
                                            <ClockIcon className="w-5 h-5" />
                                            <span className="text-xs">{formatDuration(maxDurationInterview)}</span>
                                        </div>
                                    </div>

                                    {/* Loading Indicator */}
                                    {status === 'connecting' && !mediaStream && (
                                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 text-white z-10">
                                            <Loader className="h-8 w-8 animate-spin" />
                                            <span className="mt-2 text-sm">Loading video...</span>
                                        </div>
                                    )}

                                    {/* Overlay if Camera Access Denied/Failed */}
                                    {(!cameraAccessGranted || !micAccessGranted) && (
                                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/90 text-white p-4 z-10">
                                            {/* Icon Display */}
                                            <div className="flex flex-row space-x-2 mb-3">
                                                {!cameraAccessGranted && (
                                                    <VideoOffIcon className="h-12 w-12 text-red-400" />
                                                )}
                                                {!micAccessGranted && (
                                                    <MicOffIcon className="h-12 w-12 text-red-400" />
                                                )}
                                            </div>

                                            {/* Message Display */}
                                            <p className="font-semibold mb-1">
                                                {!cameraAccessGranted && !micAccessGranted
                                                    ? "Camera & Microphone Access Needed"
                                                    : !cameraAccessGranted
                                                        ? "Camera Access Needed"
                                                        : "Microphone Access Needed"}
                                            </p>

                                            <p className="text-sm text-center">
                                                {permissionPrompt ?? "Please grant permission in your browser to start the interview."}
                                            </p>
                                        </div>
                                    )}

                                    {/* Status indicators */}
                                    <div className="absolute flex flex-row top-3 right-3 z-20 space-x-2">
                                        {/* Mic Status Indicator */}
                                        <div
                                            className={`flex items-center gap-1.5 px-2.5 py-1 opacity-80 rounded-full text-xs font-medium transition-colors backdrop-blur-sm ${micAccessGranted
                                                ? "bg-green-600/70 text-white"
                                                : "bg-red-600/70 text-white"
                                                }`}
                                            title={micAccessGranted ? "Microphone is active" : mediaError?.includes("microphone") ? mediaError : "Microphone access needed or failed"}
                                        >
                                            {micAccessGranted ? (
                                                <MicrophoneIcon className="h-3.5 w-3.5" />
                                            ) : (
                                                <MicOffIcon className="h-3.5 w-3.5" />
                                            )}
                                            <span>{micAccessGranted ? "Mic On" : "Mic Off"}</span>
                                        </div>

                                        {/* Video Status Indicator */}
                                        <div
                                            className={`flex items-center gap-1.5 px-2.5 py-1 opacity-80 rounded-full text-xs font-medium transition-colors backdrop-blur-sm ${cameraAccessGranted
                                                ? "bg-green-600/70 text-white"
                                                : "bg-red-600/70 text-white"
                                                }`}
                                            title={cameraAccessGranted ? "Camera is active" : mediaError?.includes("camera") ? mediaError : "Camera access needed or failed"}
                                        >
                                            {cameraAccessGranted ? (
                                                <VideoIcon className="h-3.5 w-3.5" />
                                            ) : (
                                                <VideoOffIcon className="h-3.5 w-3.5" />
                                            )}
                                            <span>{cameraAccessGranted ? "Camera On" : "Camera Off"}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="sm:hidden grid grid-cols-3 gap-2">
                                    <div className="cursor-pointer" onClick={() => setIsOpenDialogMic(true)}>
                                        <MicrophoneSelector
                                            label={audioInputName}
                                            className="text-sm w-full"
                                        />
                                    </div>
                                    <div className="cursor-pointer" onClick={() => setIsOpenDialogSpeaker(true)}>
                                        <SpeakerSelector
                                            label={audioOutputName}
                                            className="text-sm w-full"
                                        />
                                    </div>
                                    <div
                                        className={cn("cursor-pointer", { "pointer-events-none opacity-50": cameraSwitching })}
                                        onClick={() => { if (!cameraSwitching) setIsOpenDialogCamera(true); }}
                                    >
                                        <CameraSelector
                                            label={videoName}
                                            className="text-sm w-full"
                                        />
                                    </div>
                                </div>
                                {/* Device Selectors */}
                                <div className="hidden sm:grid grid-cols-3 gap-2">
                                    {/* Microphone */}
                                    <DropdownMenu open={isMicDropdownOpen} onOpenChange={setIsMicDropdownOpen}>
                                        <DropdownMenuTrigger asChild disabled={audioInputDevices.length === 0}>
                                            <div> {/* Wrap for Tooltip */}
                                                <MicrophoneSelector
                                                    label={audioInputName}
                                                    className="text-sm w-full"
                                                />
                                            </div>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="start" side="bottom" className="w-[420px] p-0">
                                            <div className="p-2">
                                                {audioInputDevices.map((device) => {
                                                    const isSelected = selectedAudioInputId === device.deviceId;
                                                    return (
                                                        <DropdownMenuItem
                                                            key={device.deviceId}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                handleMicrophoneSelect(device)
                                                            }}
                                                            className="cursor-pointer text-sm"
                                                            data-selected={selectedAudioInputId === device.deviceId ? 'true' : undefined}
                                                        >
                                                            {isSelected && (
                                                                <CheckIcon className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                                                            )}
                                                            <span className={cn("flex-1", {
                                                                "text-blue-600 dark:text-blue-400 font-medium": isSelected,
                                                                "text-foreground": !isSelected,
                                                            })}>
                                                                {device.label || `Microphone ${device.deviceId.slice(0, 5)}`}
                                                            </span>
                                                        </DropdownMenuItem>
                                                    )
                                                })}
                                                {audioInputDevices.length === 0 && (
                                                    <DropdownMenuItem disabled>No microphones found</DropdownMenuItem>
                                                )}
                                            </div>
                                        </DropdownMenuContent>
                                    </DropdownMenu>

                                    {/* Speaker */}
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild disabled={audioOutputDevices.length === 0}>
                                            <div>
                                                <SpeakerSelector
                                                    label={audioOutputName}
                                                    className="text-sm w-full"
                                                />
                                            </div>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="start" side="bottom" className="w-[420px] p-0">
                                            {audioOutputDevices.map((device) => {
                                                const isSelected = selectedAudioOutputId === device.deviceId;
                                                return (
                                                    <DropdownMenuItem
                                                        key={device.deviceId}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            handleSpeakerSelect(device)
                                                        }}
                                                        className="cursor-pointer text-sm"
                                                        data-selected={selectedAudioOutputId === device.deviceId ? 'true' : undefined}
                                                        disabled={!('setSinkId' in HTMLMediaElement.prototype)}
                                                    >
                                                        {isSelected && (
                                                            <CheckIcon className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                                                        )}
                                                        <span className={cn("flex-1", {
                                                            "text-blue-600 dark:text-blue-400 font-medium": isSelected,
                                                            "text-foreground": !isSelected,
                                                        })}>
                                                            {device.label || `Speaker ${device.deviceId.slice(0, 5)}`}
                                                        </span>
                                                    </DropdownMenuItem>
                                                )
                                            })}
                                            {audioOutputDevices.length === 0 && (
                                                <DropdownMenuItem disabled>No speakers found</DropdownMenuItem>
                                            )}
                                            {!('setSinkId' in HTMLMediaElement.prototype) && audioOutputDevices.length > 0 && (
                                                <DropdownMenuItem disabled>Speaker selection not supported</DropdownMenuItem>
                                            )}
                                        </DropdownMenuContent>
                                    </DropdownMenu>

                                    {/* Camera */}
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild disabled={videoDevices.length === 0 || cameraSwitching}>
                                            <div className={cn({ "pointer-events-none opacity-50": cameraSwitching })}>
                                                <CameraSelector
                                                    label={videoName}
                                                    className="text-sm w-full"
                                                />
                                            </div>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="start" side="bottom" className="w-[420px] p-0">
                                            {videoDevices.map((device) => {
                                                const isSelected = selectedVideoId === device.deviceId;
                                                return (
                                                    <DropdownMenuItem
                                                        key={device.deviceId}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            handleCameraSelect(device)
                                                        }}
                                                        className="cursor-pointer text-sm"
                                                        data-selected={selectedVideoId === device.deviceId ? 'true' : undefined}
                                                    >
                                                        {isSelected && (
                                                            <CheckIcon className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                                                        )}
                                                        <span className={cn("flex-1", {
                                                            "text-blue-600 dark:text-blue-400 font-medium": isSelected,
                                                            "text-foreground": !isSelected,
                                                        })}>
                                                            {device.label || `Camera ${device.deviceId.slice(0, 5)}`}
                                                        </span>
                                                    </DropdownMenuItem>
                                                )
                                            })}
                                            {videoDevices.length === 0 && (
                                                <DropdownMenuItem disabled>No cameras found</DropdownMenuItem>
                                            )}
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>

                                {/* Always-visible audio level indicator (ticket #1870).
                                    Relocated from inside the microphone dropdown so candidates
                                    have a persistent readout of their mic level before starting. */}
                                <div className="rounded-lg border border-border bg-card">
                                    <AudioLevelIndicator
                                        deviceId={selectedAudioInputId || undefined}
                                        onStatusChange={setWaitingRoomAudioStatus}
                                        isVirtualMicSelected={isVirtualAudioDevice(
                                            audioInputDevices.find((d) => d.deviceId === selectedAudioInputId)?.label,
                                        )}
                                    />
                                </div>
                            </div>
                            {/* Right side - Status & Actions */}
                            <div className="flex flex-col gap-6 items-center justify-center w-full mb-24 sm:mb-0">
                                <div className="w-full sm:w-[440px]">
                                    <div className="w-full">
                                        <h2 className="text-2xl md:text-xl font-semibold mt-0 sm:mt-4 md:mt-0 mb-4">
                                            {requestSubmitted ? t("Request Submitted", "Permintaan Dikirim", "Permintaan Dihantar") :
                                                blockInterview ? t("Access Blocked", "Akses Diblokir", "Akses Disekat") :
                                                    (headlineReady ?? t("Get ready for your AI interview", "Persiapkan diri Anda untuk wawancara AI", "Sedia untuk temuduga AI anda"))}
                                        </h2>

                                            {!requestSubmitted && !blockInterview && (
                                                <div className="space-y-4 mb-0">
                                                <div className="flex items-center gap-3">
                                                    <StarIcon className="h-4 w-4 text-[#2563eb] flex-shrink-0" />
                                                    <span className="text-sm text-gray-700 dark:text-gray-300">
                                                        {readinessBullets?.[0] ?? t("Start now or come back later", "Mulai sekarang atau kembali lagi nanti", "Mula sekarang atau kembali kemudian")}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <TrendingUpIcon className="h-4 w-4 text-[#2563eb] flex-shrink-0" />
                                                    <span className="text-sm text-gray-700 dark:text-gray-300">
                                                        {readinessBullets?.[1] ?? ((language === "Demo")
                                                            ? t("Practice freely and get instant feedback", "Latihan dengan bebas dan dapatkan penilaian langsung", "Berlatih dengan bebas dan dapatkan maklum balas segera")
                                                            : t("Stand out beyond a CV and get noticed faster", "Tampilkan potensi terbaik Anda, bukan hanya dari CV", "Tonjolkan diri melebihi CV dan dikenali lebih cepat"))}

                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <SettingsIcon className="h-4 w-4 text-[#2563eb] flex-shrink-0" />
                                                    <span className="text-sm text-gray-700 dark:text-gray-300">
                                                        {readinessBullets?.[2] ?? t("Check your camera, microphone, and internet connection", "Periksa kamera, mikrofon, dan koneksi internet Anda", "Semak kamera, mikrofon, dan sambungan internet anda")}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <WifiIcon className="h-4 w-4 text-[#2563eb] flex-shrink-0" />
                                                    <span className="text-sm text-gray-700 dark:text-gray-300">
                                                        {readinessBullets?.[3] ?? t("Find a quiet place with stable internet", "Temukan tempat yang tenang dengan koneksi internet stabil", "Cari tempat yang tenang dengan internet yang stabil")}
                                                    </span>
                                                </div>
                                                    {/* Time-to-join row moved into the SETUP CHECKS panel below. */}
                                            </div>
                                        )}

                                        <div className="mb-3">
                                            {/* Informational Messages */}
                                            <div className="mb-3 w-full">
                                                {/* Request Submitted Message */}
                                                {requestSubmitted && (
                                                    <div className={`${primaryColorClasses.bgLight} ${primaryColorClasses.bgLightDark} border ${primaryColorClasses.border} ${primaryColorClasses.borderDark} rounded-lg p-3 w-full`}>
                                                        <p className={`text-sm ${primaryColorClasses.text} ${primaryColorClasses.textDark}`}>
                                                            {t(
                                                                `We're reviewing your ${requestType} request and will email you shortly.`,
                                                                `Kami sedang meninjau permintaan ${requestType} Anda dan akan mengirim email segera.`,
                                                                `Kami sedang menyemak permintaan ${requestType} anda dan akan menghantar e-mel tidak lama lagi.`
                                                            )}
                                                        </p>
                                                    </div>
                                                )}

                                                {/* Blocked / Expired Messages */}
                                                {!requestSubmitted && (interviewExpired || blockInterview) && (
                                                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 rounded-lg p-3 w-full mt-4">
                                                        <p className="text-sm text-red-600 dark:text-red-400">
                                                            {blockInterview
                                                                ? (internalErrorMessage || t("Access blocked. Refreshing may end the session.", "Akses diblokir. Memuat ulang dapat mengakhiri sesi.", "Akses disekat. Memuat semula mungkin menamatkan sesi."))
                                                                : <>
                                                                    {expiredBodyText ?? t("This interview session has expired.", "Sesi wawancara ini telah kedaluwarsa.", "Sesi temuduga ini telah tamat tempoh.")}
                                                                    <br />
                                                                    {t("You did not join within the allocated time.", "Anda tidak bergabung dalam waktu yang ditentukan.", "Anda tidak menyertai dalam masa yang ditetapkan.")}
                                                                </>}
                                                        </p>
                                                    </div>
                                                )}

                                                {/* createInterview registration failure — stops the flow
                                                    and offers a retry. Takes precedence over the waiting
                                                    spinner since none of those next steps will run until
                                                    the registration succeeds. */}
                                                {!requestSubmitted && !interviewExpired && !blockInterview && createInterviewError && (
                                                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 rounded-lg p-3 w-full space-y-3">
                                                        <div>
                                                            <p className="text-sm font-medium text-red-700 dark:text-red-300">
                                                                {t(
                                                                    "We couldn't start your interview session.",
                                                                    "Kami tidak dapat memulai sesi wawancara Anda.",
                                                                    "Kami tidak dapat memulakan sesi temuduga anda."
                                                                )}
                                                            </p>
                                                            <p className="text-xs text-red-600 dark:text-red-400 mt-1 break-words">
                                                                {createInterviewError}
                                                            </p>
                                                        </div>
                                                        <Button
                                                            onClick={handleRetryCreateInterview}
                                                            disabled={isRetryingCreateInterview}
                                                            className="w-full bg-red-600 hover:bg-red-700 text-white"
                                                        >
                                                            {isRetryingCreateInterview
                                                                ? t("Retrying…", "Mencoba lagi…", "Mencuba semula…")
                                                                : t("Retry", "Coba lagi", "Cuba semula")}
                                                        </Button>
                                                    </div>
                                                )}

                                            </div>
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="w-full space-y-3 sm:space-y-2 flex flex-col items-center mt-4 sm:mt-0 gap-2">
                                                {/* TODO: temporarily disabled while the new Setup Checks panel
                                                handles the same surface area. Re-enable (or remove for good)
                                                once we've decided whether the inline warning is still needed.
                                                {permissionResolved && (
                                                    <MediaPermissionWarning
                                                        cameraGranted={cameraAccessGranted}
                                                        micGranted={micAccessGranted}
                                                    />
                                                )}
                                                */}

                                                {/* Setup Checks panel — sits just above the Start button so
                                                the candidate sees per-item status (mic / camera / network)
                                                plus a one-tap Enable that re-runs the device probe. */}
                                                {!requestSubmitted && !interviewExpired && !blockInterview && (
                                                    <WaitingRoomSetupChecks
                                                        cameraGranted={cameraAccessGranted}
                                                        micGranted={micAccessGranted}
                                                        onCameraGranted={() => { setCameraAccessGranted(true); setMediaError(null); }}
                                                        onMicGranted={() => { setMicAccessGranted(true); setMediaError(null); }}
                                                        onPermissionError={(msg) => setMediaError(msg)}
                                                        connectionQuality={connectionQuality}
                                                        audioLevelStatus={waitingRoomAudioStatus}
                                                        // Label-based virtual-mic detection. Combined with the
                                                        // audio-level signal inside the panel to keep the
                                                        // warning stable when a virtual mic occasionally picks
                                                        // up system sound.
                                                        isVirtualMicSelected={isVirtualAudioDevice(
                                                            audioInputDevices.find((d) => d.deviceId === selectedAudioInputId)?.label,
                                                        )}
                                                        interviewLanguage={interviewLanguage}
                                                        showTimeToJoin={cameraAccessGranted && micAccessGranted && AiJoined && countdownActive}
                                                        timeoutMinutes={timeoutMinutes}
                                                        onTimeout={handleTimeout}
                                                        onReportIssue={() => setIsIssuesDialogOpen(true)}
                                                    />
                                                )}
                                            {!requestSubmitted ? (
                                                <>
                                                    {/* Retake Button (Only when expired/blocked) */}
                                                    {(interviewExpired || blockInterview) ? (
                                                        <Button
                                                            onClick={() => {
                                                                if (!onReload) {
                                                                    setTimeout(() => {
                                                                        window.location.reload(); // For now, just reload to reset state
                                                                    }, 2000)
                                                                } else {
                                                                    onReload();
                                                                }
                                                            }}
                                                            className={`${primaryColorClasses.bg} ${primaryColorClasses.bgHover} text-white py-6 rounded-full w-full`}
                                                        >
                                                            {retakeLabel ?? t("Retake Interview", "Ulangi Wawancara", "Ulang Temuduga")}
                                                        </Button>
                                                    ) : (
                                                        /* Start Interview Button */
                                                        <Button
                                                            onClick={handleStartInterviewClick}
                                                            disabled={!cameraAccessGranted || !micAccessGranted || !isOnline || !AiJoined || blockInterview || status === 'connecting' || !isBrowserSupported || isLoadingJoin} className={` w-full text-white py-[21px] rounded-full transition-opacity ${(cameraAccessGranted && micAccessGranted && isOnline && AiJoined && !blockInterview && status !== 'connecting' && isBrowserSupported)
                                                                ? `${primaryColorClasses.bg} ${primaryColorClasses.bgHover} cursor-pointer`
                                                                : "bg-gray-400 dark:bg-gray-600 cursor-not-allowed opacity-70"
                                                                }`}
                                                            title={getButtonTooltip()}
                                                        >
                                                            {isLoadingJoin
                                                                ? (loadingActionLabel ?? t("Starting Interview...", "Memulai wawancara...", "Memulakan temuduga..."))
                                                                : (!cameraAccessGranted || !micAccessGranted || !isOnline)
                                                                    ? t("Resolve the issues above to start", "Atasi masalah di atas untuk mulai", "Selesaikan isu di atas untuk mula")
                                                                    : !AiJoined
                                                                        ? <span className="flex items-center justify-center gap-2"><Loader className="h-4 w-4 animate-spin" />{t("Awaiting AI Interviewer...", "Menunggu Pewawancara AI...", "Menunggu Penemuduga AI...")}</span>
                                                                        : (actionLabel ?? t("Start Interview", "Mulai wawancara", "Mula Temuduga"))}

                                                        </Button>
                                                    )}

                                                    {/* Secondary actions row — "I will do this later" (opt-in
                                                        via `showSkipLaterButton`, used by JobseekerSignup) sits
                                                        side-by-side with "I'm having issues". When only one of
                                                        them is rendered it expands to fill the row. */}
                                                    {((showSkipLaterButton && onSkipInterview) ||
                                                        !(interviewExpired || blockInterview || isDisabledButtonIssue)) && (
                                                        <div className="flex justify-center items-center gap-3 w-full">
                                                            {showSkipLaterButton && onSkipInterview && (
                                                                <Button
                                                                    variant="outline"
                                                                    disabled={isLoadingSkipInterview}
                                                                    className="flex-1 bg-transparent border-gray-300 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-full py-[21px]"
                                                                    onClick={onSkipInterview}
                                                                >
                                                                    {isLoadingSkipInterview
                                                                        ? t("Saving...", "Menyimpan...", "Menyimpan...")
                                                                        : t("I will do this later", "Saya akan melakukannya nanti", "Saya akan buat ini kemudian")}
                                                                </Button>
                                                            )}
                                                            {!(interviewExpired || blockInterview || isDisabledButtonIssue) && (
                                                                <Button
                                                                    variant="outline"
                                                                    onClick={() => {
                                                                        // Waiting-room trigger — flag the report context
                                                                        // before opening the dialog so the bug report
                                                                        // meta records `type: "waiting-room"`.
                                                                        setReportTypeIn("waiting-room");
                                                                        setIsIssuesDialogOpen(true);
                                                                    }}
                                                                    className="flex-1 bg-transparent border-gray-300 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-full py-[21px]"
                                                                >
                                                                    {t("I'm having issues", "Saya mengalami masalah", "Saya mengalami masalah")}
                                                                </Button>
                                                            )}
                                                        </div>
                                                    )}

                                                        {privacyLine1 || privacyLine2 ? (
                                                        // Caller-provided custom privacy copy (e.g. training mode) keeps
                                                        // the original two-line layout.
                                                            <p className="text-xs text-gray-500 dark:text-gray-400 text-center leading-relaxed">
                                                                {privacyLine1}
                                                                {privacyLine1 && privacyLine2 && <br />}
                                                                {privacyLine2}
                                                            </p>
                                                        ) : (
                                                            <p className="w-full self-start text-xs text-gray-500 dark:text-gray-400 text-left leading-relaxed">
                                                                <span className="font-semibold text-gray-700 dark:text-gray-300">
                                                                    {t("Privacy:", "Privasi:", "Privasi:")}
                                                                </span>{" "}
                                                                {t(
                                                                    "Your responses are only used to evaluate your candidacy. They are not used to train AI models.",
                                                                    "Respons Anda hanya digunakan untuk menilai kelayakan Anda sebagai kandidat. Respons tidak digunakan untuk melatih model AI.",
                                                                    "Respons anda hanya digunakan untuk menilai kelayakan anda. Respons tidak digunakan untuk melatih model AI."
                                                                )}
                                                                </p>
                                                        )}
                                                    {isSkipInterview && (
                                                        <div className="flex flex-row justify-center">
                                                            <Button
                                                                variant="outline"
                                                                disabled={isLoadingSkipInterview}
                                                                onClick={onSkipInterview}
                                                                className="w-full flex items-center gap-2 mt-4"
                                                            >
                                                                {isLoadingSkipInterview ? (
                                                                    <>
                                                                        <svg className="animate-spin h-4 w-4 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                        </svg>
                                                                        Processing
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <ClockIcon className="h-4 w-4" />
                                                                        I want to do this later
                                                                    </>
                                                                )}

                                                            </Button>
                                                        </div>
                                                    )}
                                                </>
                                            ) : (
                                                <Button
                                                    onClick={onCloseModal}
                                                    variant="outline"
                                                    className="w-full rounded-full"
                                                >
                                                    Close
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div >
                )}
                {/* Pre-interview audio gate modal (ticket #1870).
                    Soft variant: weak audio — user may continue anyway.
                    Hard variant: no audio — user must check microphone first. */}
                <AudioGateModal
                    variant={audioGateVariant ?? "soft"}
                    open={audioGateVariant !== null}
                    onClose={() => setAudioGateVariant(null)}
                    onContinueAnyway={() => {
                        void proceedPastAudioGate();
                    }}
                    onCheckMicrophone={openMicrophonePicker}
                    t={t as (en: string, id: string, ms: string) => string | React.ReactNode}
                />

                {
                    showConfirmationModal && (
                        <InterviewConfirmationModal
                            onClose={() => setShowConfirmationModal(false)}
                            onConfirm={handleConfirmInterview}
                            loading={isRecordingStarting}
                            interviewLanguage={interviewLanguage}
                            language={language}
                            companyName={companyInfo?.company_name}
                            colorScheme={'default'}
                        />
                    )
                }

                {
                    isDialogConfirmEndInterview && (
                        <InterviewEndConfirmDialogV2
                            isOpen={isDialogConfirmEndInterview}
                            isTestMode={isTestInterview}
                            isEarlyExit={maxDurationInterview > 0 && (interviewTimeDuration / maxDurationInterview) * 100 >= 51}
                            remainingMinutes={Math.ceil((maxDurationInterview - interviewTimeDuration) / 60)}
                            onClose={() => setIsDialogConfirmEndInterview(false)}
                            onConfirm={onHandleEndConfirmInterview}
                            sessionMode={sessionMode}
                        />
                    )
                }

                <RetakeRequestDialog
                    isOpen={isRetakeDialogOpen}
                    onClose={() => setIsRetakeDialogOpen(false)}
                    onSubmit={handleRetakeRequest}
                    userId={userId}
                />

                <IssuesDialog
                    userId={userId}
                    assessmentId={interviewId}
                    isOpen={isIssuesDialogOpen}
                    onClose={() => setIsIssuesDialogOpen(false)}
                    onSubmit={handleIssueSubmit}
                    colorScheme={'default'}
                    type={reportTypeIn}
                    selectedAudioDeviceID={selectedAudioInputId || undefined}
                    selectedVideoDeviceID={selectedVideoId || undefined}
                />

                <Dialog open={isOpenDialogMic} onOpenChange={() => setIsOpenDialogMic(false)}>
                    <DialogContent className="sm:max-w-md p-2" closeButtonClassName="top-4 right-4">
                        <DialogHeader className="flex flex-col text-left pt-4 pl-2">
                            <DialogTitle className="text-xl font-semibold">Select Microphone</DialogTitle>
                        </DialogHeader>
                        <div>
                            {audioInputDevices.map((device) => {
                                const isSelected = selectedAudioInputId === device.deviceId;
                                return (
                                    <button
                                        key={device.deviceId}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleMicrophoneSelect(device)
                                        }}
                                        className="cursor-pointer text-sm flex items-center gap-2 p-2"
                                        data-selected={selectedAudioInputId === device.deviceId ? 'true' : undefined}
                                    >
                                        {isSelected && (
                                            <CheckIcon className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                                        )}
                                        <span className={cn("flex", {
                                            "text-blue-600 dark:text-blue-400 font-medium": isSelected,
                                            "text-foreground": !isSelected,
                                        })}>
                                            {device.label || `Microphone ${device.deviceId.slice(0, 5)}`}
                                        </span>
                                    </button>
                                )
                            })}
                            {audioInputDevices.length === 0 && (
                                <div>No microphones found</div>
                            )}
                        </div>
                    </DialogContent>
                </Dialog>

                <Dialog open={isOpenDialogSpeaker} onOpenChange={() => setIsOpenDialogSpeaker(false)}>
                    <DialogContent className="sm:max-w-md p-2" closeButtonClassName="top-4 right-4">
                        <DialogHeader className="flex flex-col text-left pt-4 pl-2">
                            <DialogTitle className="text-xl font-semibold">Select Speaker</DialogTitle>
                        </DialogHeader>
                        {audioOutputDevices.map((device) => {
                            const isSelected = selectedAudioOutputId === device.deviceId;
                            return (
                                <button
                                    key={device.deviceId}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleSpeakerSelect(device)
                                    }}
                                    className="cursor-pointer text-sm flex items-center gap-2 p-2"
                                    data-selected={selectedAudioOutputId === device.deviceId ? 'true' : undefined}
                                    disabled={!('setSinkId' in HTMLMediaElement.prototype)}
                                >
                                    {isSelected && (
                                        <CheckIcon className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                                    )}
                                    <span className={cn("flex", {
                                        "text-blue-600 dark:text-blue-400 font-medium": isSelected,
                                        "text-foreground": !isSelected,
                                    })}>
                                        {device.label || `Speaker ${device.deviceId.slice(0, 5)}`}
                                    </span>
                                </button>
                            )
                        })}
                        {audioOutputDevices.length === 0 && (
                            <div>No speakers found</div>
                        )}
                        {!('setSinkId' in HTMLMediaElement.prototype) && audioOutputDevices.length > 0 && (
                            <div>Speaker selection not supported</div>
                        )}
                    </DialogContent>
                </Dialog>

                <Dialog open={isOpenDialogCamera} onOpenChange={() => setIsOpenDialogCamera(false)}>
                    <DialogContent className="sm:max-w-md p-2" closeButtonClassName="top-4 right-4">
                        <DialogHeader className="flex flex-col text-left pt-4 pl-2">
                            <DialogTitle className="text-xl font-semibold">Select Camera</DialogTitle>
                        </DialogHeader>
                        {videoDevices.map((device) => {
                            const isSelected = selectedVideoId === device.deviceId;
                            return (
                                <button
                                    key={device.deviceId}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleCameraSelect(device)
                                    }}
                                    disabled={cameraSwitching}
                                    className={cn("cursor-pointer text-sm flex items-start flex-row justify-start gap-2 p-2", { "opacity-50 cursor-not-allowed": cameraSwitching })}
                                    data-selected={selectedVideoId === device.deviceId ? 'true' : undefined}
                                >
                                    {isSelected && (
                                        <CheckIcon className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                                    )}
                                    <div className={cn("flex", {
                                        "text-blue-600 dark:text-blue-400 font-medium": isSelected,
                                        "text-foreground": !isSelected,
                                    })}>
                                        {device.label || `Camera ${device.deviceId.slice(0, 5)}`}
                                    </div>
                                </button>
                            )
                        })}
                        {videoDevices.length === 0 && (
                            <div>No cameras found</div>
                        )}
                    </DialogContent>
                </Dialog>
            </>
        )
    });

// Add display name for better debugging
CustomVoiceInterviewV2.displayName = "CustomVoiceInterviewV2";
export default CustomVoiceInterviewV2;